/**
 * Every word the widget says on its own behalf.
 *
 * Static and versioned with the build, never generated: these are the strings
 * shown when the model is absent, refused, or has not spoken yet, and a
 * generated one would reach the screen through the channel no gate reads.
 *
 * No string here contains a digit. A figure carries a unit and an as-of and
 * comes from the engine; a numeral baked into a label has neither.
 *
 * `docs/2026-08-11-nepali-guardrail-lexicons.md` is *not* a source for this
 * table, and that is worth saying explicitly rather than leaving as a gap
 * nobody explained. That document covers a different problem entirely: S1
 * question-side intent markers and S3 answer-side drift phrases, i.e. how to
 * *detect* buy/sell/ranking/forecast language inside a turn. None of its
 * ground overlaps a composer placeholder, a button label, or a disclosure
 * sentence. Even where a fragment looks reusable on its face — e.g. the
 * quoted refusal shape "kinnu ramro ho ki hoina bhanna sakdina" — the
 * document's own header calls the whole file "awaiting a Nepali speaker's
 * review," and its entries are normalised/diacritic-stripped forms built for
 * substring matching, not display-quality prose. Lifting any of it into
 * shipped copy would be exactly the unreviewed machine-shaped translation
 * this module exists to refuse.
 *
 * So every non-English entry below is unsourced, and `AWAITING_TRANSLATION`
 * below is the asserted record of that — the suite fails if a key is
 * translated without being removed from it, or listed in it while already
 * translated, so the inventory cannot drift from the catalogue the way a
 * comment could. See
 * `.superpowers/sdd/2026-08-19-mitra-widget-slice-1/task-6-report.md` for the
 * full accounting.
 */
import type { TurnScript } from '@/features/mitra/api/frames'

export const LANGS = ['en', 'ne', 'ne-latn'] as const
export type Lang = (typeof LANGS)[number]

export interface Copy {
  ask: string
  /** The composer's placeholder once a conversation exists — Figma's every
   *  state with a turn already on screen (2026-09-28 design refresh), where
   *  `ask` is the empty-panel invitation instead. */
  askFollowUp: string
  send: string
  /** The send control while a turn is in flight. See `micStop` for the word. */
  stopAnswer: string
  /** The NEPSE index card's range ends, its two range titles' frame, and its kicker. */
  factsLow: string
  factsHigh: string
  factsTodaysRange: string
  factsTradingRanges: string
  factsIndex: string
  /**
   * The peer strip's tick for the group's own mark, where the engine sent no
   * word for it: its middle value, or its mean. Never a company.
   */
  groupMedian: string
  groupAverage: string
  jumpToLatest: string
  /** Read aloud with the what-if slider: how to ask the move it is set to. */
  whatIfHint: string
  /** The what-if slider's button, which asks the move it is set to. */
  whatIfAsk: string
  /** The small caps line over the next-step chip, so it reads as a control. */
  askNext: string
  suggestTry: string
  suggestMore: string
  deskMarket: string
  /** A question a tap sends as worded. Swept: a change here must be made in `sweptAsks.json` too. */
  deskAskMarket: string
  deskBreadth: string
  deskBook: string
  deskBookToday: string
  /** A question a tap sends as worded. Swept: a change here must be made in `sweptAsks.json` too. */
  deskAskBook: string
  deskAddHoldings: string
  deskCompanies: string
  deskLearn: string
  deskRemindersBoard: string
  deskPinned: string
  deskNoReminder: string
  deskContinue: string
  deskStrip: string
  deskExamples: readonly string[]
  deskTry: string
  /** The companies tile's title where the engine drew companies to explore, not the reader's own. */
  deskCompaniesExplore: string
  /** A company row's accessible name; `{name}` is the company, or its ticker. */
  deskOpenCompany: string
  /** The lead line's mark, named for a screen reader (`panel/LeadMark.tsx`). */
  /** A marked move's direction, said where its triangle is drawn. */
  markUp: string
  markDown: string
  /**
   * A marked company name's accessible name: `{name}` is the word as written.
   * A tap opens the company's quick sheet, so it says "shows details".
   */
  markOpensCompany: string
  /**
   * A `link` mark's accessible name (wire §13): `{name}` is the words as
   * written, `{page}` one of the `markPage*` names below.
   */
  markOpensPage: string
  markPageIndex: string
  markPageSector: string
  markPageBroker: string
  markPageIpo: string
  markPageTerm: string
  markPageSignalZone: string
  leadUp: string
  leadDown: string
  leadDates: string
  leadLearn: string
  /** The Learn tile's call to action, beside an arrow. */
  deskLearnTap: string
  /** The upcoming-events tile's title. */
  deskUpcoming: string
  /** The two lines `events: []` draws: how the tile fills itself. */
  deskPinHint: string
  deskRemindHint: string
  /** A count chip on the upcoming tile; `{n}` is the number of reminders kept. */
  deskReminderCount: string
  /** Each event kind as the upcoming tile names it. */
  deskEventBookClosure: string
  deskEventAgm: string
  deskEventRight: string
  deskEventIpo: string
  /** The dashed line under the sparkline: the session before's level, a server string. */
  deskPrevClose: string
  /**
   * What was on record beside a move: the company page's card title, and the
   * name of the lines under a desk move. Its lines and its question are the
   * server's own.
   */
  whyItMoved: string
  /**
   * The row under a finished answer (`panel/AnswerActions.tsx`): its group
   * name, Copy and its two outcomes, and Listen with its stop and its failure.
   * The engine's own widget reads a spoken question's answer back unasked and
   * has no such row, so these are new words; `micStop`'s "Stop" and
   * `answerSpeaking`'s wait are the widget's and are reused. Every failure is
   * worded as a fact about the voice, never as the reader's error: the answer
   * is still on screen.
   */
  answerActions: string
  answerCopy: string
  answerCopied: string
  answerCopyFailed: string
  answerListen: string
  answerListenStop: string
  answerVoiceFailed: string
  /**
   * The waiting caption while a tool is reading the reader's own holdings.
   *
   * One of seven phase captions (`lib/phase.ts`), and the only one that
   * predates them — it was the single fixed line shown for the whole of a
   * turn, including the seconds before any tool had run and could have
   * reported a portfolio at all.
   */
  reading: string
  /**
   * The waiting caption while a statement is on its way to the server.
   *
   * Not one of `lib/phase.ts`'s phase captions, and shown before any of
   * them can apply: those all name a tool that ran inside a turn, and no
   * turn exists yet while a file is still uploading. `read_statement`'s own
   * turn is captioned by `reading` once it starts (`lib/phase.ts` maps
   * `read_statement` there) — this key covers only the request that
   * precedes it.
   */
  uploading: string
  /**
   * Before any tool has run: the honest answer to "what is it doing".
   *
   * Names no source, because none has answered yet. Deliberately not
   * "thinking about your portfolio" — nothing has read one.
   */
  phaseThinking: string
  /** A tool read the exchange's own data: prices, fundamentals, the register. */
  phaseMarket: string
  /**
   * A tool read the news.
   *
   * Separate from `phaseMarket` because the feed is a publisher's, not
   * NEPSE's, and one caption over both would put the exchange's name on a
   * third party's article.
   */
  phaseNews: string
  /** A tool read the educational corpus — how a rule works, not what a price is. */
  phaseRules: string
  /** A tool computed fees or tax. Arithmetic, not a reading. */
  phaseFigures: string
  /**
   * A tool ran that the caption map does not know.
   *
   * Says only that work is happening. A new tool must not be able to make
   * this surface claim a source by default — the failure the header shipped
   * once already, stating a market session no tool had reported.
   */
  phaseWorking: string
  /**
   * The model is composing the answer: no tool call is under way.
   *
   * This and the two below joined the waiting list (`lib/steps.ts`, owner,
   * 2026-10-26) worded as the engine's reference client words them.
   */
  phaseWriting: string
  /**
   * The finished answer is being rewritten in Mitra's own voice: the voice
   * pass, announced as `writing` with the family `voice` (engine,
   * 2026-10-26), and the longest wait of a turn that has one.
   */
  phaseVoice: string
  /** A recovery round is re-reading a figure before it reaches the reader. */
  phaseChecking: string
  loading: string
  answerFailed: string
  // "Incomplete", not "failed": the sentences already on screen are true and
  // stay. What stopped is the rest of them.
  answerIncomplete: string
  disclosureTitle: string
  disclosureBody: string
  disclosureAccept: string
  /** The switch, on a chart's head, that draws gains and losses in blue and orange. */
  colourBlind: string
  /** The title and button name of a chart's colour-blind control. */
  colourBlindTitle: string
  /** The line under that switch: what it does. */
  colourBlindNote: string
  sourceYourPortfolio: string
  headerMore: string
  headerNote: string
  unreachable: string
  noHoldings: string
  nothingHere: string
  /**
   * A session that ended: a token was held, the turn was refused, the host
   * was asked for a fresh one and did not answer.
   *
   * The reload instruction is only true HERE. It is the host that mints, so
   * a page that minted once will mint again — see `sessionMissing` for the
   * state where the same sentence is a false statement and its advice cannot
   * work.
   */
  sessionExpired: string
  /**
   * The engine refused a member's own mm-be token that is still good by its
   * own `exp` (2026-10-27: the deployed engine 401s every dev token). Not
   * `sessionExpired`: the session has not ended, and reloading cannot help,
   * so this says whose problem it is and asks for nothing but patience.
   */
  engineSignInFailed: string
  /**
   * The chat page for a reader Mitra is not open to yet (the engine's 403
   * `not_in_rollout`, or `GET /v1/access` saying no), drawn instead of the chat.
   */
  notInRollout: string
  /**
   * The engine could not be reached (down, restarting, or out of its model
   * key's budget): said in place of an error screen under a question that got
   * no words, and kept with it for the reader to send again.
   */
  engineAway: string
  /** The same, on the open chat before anything is asked. */
  engineAwayIdle: string
  /**
   * A finished answer's work, folded under its head (revamp mock, 2026-10-27):
   * how many of the engine's steps it took, and how long from the question.
   * `{n}` and `{s}` are filled in the reader's digits; one step has its own.
   */
  workedThrough: string
  workedThroughOne: string
  /**
   * No session ever began: nothing has handed this frame a token at all.
   *
   * Reached by a developer opening `/embed` with no host page, and by a
   * production embed whose template did not mint one — which is why the
   * sentence names neither. "Your session ended" was drawn here too, and it
   * asserts an ending that never happened, then tells a reader to reload,
   * which reproduces the identical screen for as long as they are willing to
   * keep trying. It says nothing about reloading now: a template that failed
   * once might mint on the next render, and promising either way would be
   * inventing a fact about a page this frame cannot see.
   *
   * Shaped against `unreachable`, which is the rail's sentence for the same
   * situation, so the two halves of the widget do not disagree about what is
   * happening to them.
   */
  sessionMissing: string
  newest: string
  /** The transcript's `role="log"` accessible name — see Transcript.tsx. */
  conversationLabel: string
  /**
   * Who said this, for a reader who cannot see the bubble.
   *
   * `role="log"` announces each turn as it lands, and side, fill and a mark
   * are the whole attribution on screen — none of which reaches a screen
   * reader. Without these two the log reads as one unbroken voice asking
   * itself questions.
   */
  youAsked: string
  mitraSaid: string
  /** The head's name for a final close, beside its date. */
  closingPrice: string
  /**
   * The head's name for a company's filed accounts, beside the filing's date,
   * where the engine dates a figure by its filing rather than calling it
   * stale (`DataFrame.basis`).
   */
  filingStamp: string
  /** The market card's breadth columns: how many listings rose, fell and held. */
  marketAdvanced: string
  marketDeclined: string
  marketUnchanged: string
  /**
   * The reply frame's tag words (`genui/reply.ts` `TagWord`), drawn only
   * while `config.mitraGenui` is on. Every string below is the engine
   * widget's own, register for register, so the two surfaces say one thing.
   */
  tagMitra: string
  tagPortfolio: string
  tagCompany: string
  tagMarket: string
  tagScreen: string
  tagTax: string
  tagPlan: string
  tagLearn: string
  tagFiles: string
  /** The folded source rows. `{n}` is a count the frame formats. */
  sourcesCheckedOne: string
  sourcesCheckedMany: string
  sourcesFailed: string
  sourcesHide: string
  /** The pulse between the last lookup and the first word. */
  replyThinking: string
  /**
   * What the pulse says as that wait goes on, one after another (owner,
   * 2026-10-12: "more interesting and alluring"). Each names work the turn
   * really does at that point -- the figures are matched to the data, every
   * one is checked, the words are written -- so a long wait reads as care
   * rather than as a stall. Never a claim about the market or the reader.
   */
  replyStepFigures: string
  replyStepChecking: string
  /** The two steps of a wait where nothing is looked up (a greeting). */
  replyChatStart: string
  replyChatWords: string
  // Each of the five holds a few variants split on "|"; one is picked per
  // wait, so no line repeats under every answer.
  replyStepWords: string
  replyStepAlmost: string
  /**
   * The reply frame's withheld notice: the widget's wording. The older
   * `withheld` promises to keep to "what your holdings show" under any
   * answer, a market question's included; it stays as it is with the card
   * flag off.
   */
  replyWithheld: string
  /** Seconds a phase has lasted, once it has lasted three. `{n}` is a count. */
  secondsElapsed: string
  /**
   * The header's context strip: what this panel is currently reading.
   *
   * Only the portfolio needs a word. A company surface names the symbol
   * itself, and a symbol is machine identity — it does not translate, so it
   * is not a key here.
   */
  contextPortfolio: string
  /**
   * The stamp the header shows for the life of a refusing turn.
   *
   * E2's deck stamps `BLOCKED · REFUSED`; one word is enough here because
   * the bar above it has already changed colour. Cased in CSS, not here — a
   * language whose script has no case must not inherit an English decision
   * about capitals.
   */
  refused: string
  /** The panel-width control, named for what the next press will do. */
  widen: string
  narrow: string
  /**
   * The control that puts the panel back to the 56px tab.
   *
   * Named for the panel and not for the product — "Close Mitra" would claim
   * the widget is gone, and the tab stays on the edge of the page. It is the
   * only way out of the panel on a viewport the panel nearly fills, so it is
   * never conditional and never hidden behind a menu.
   */
  close: string
  /**
   * How much of a cut view is on screen, as a template.
   *
   * The only entry here with placeholders, and the only one that needs them:
   * both counts come from the block's own payload, so they are engine
   * figures rather than numerals baked into a label — which is the
   * distinction the no-digits rule in this module's docstring is actually
   * about. `{shown}` and `{total}` carry no digits themselves, so the rule
   * still holds literally as well.
   */
  showingOf: string
  /**
   * The control that opens a bounded default view.
   *
   * `{total}` is the envelope's own `row_count`, so the number a reader taps
   * is an engine figure rather than one counted here. Present tense and the
   * action's own name, because the sentence it becomes after the tap is the
   * list itself.
   */
  showAll: string
  /**
   * What is drawn where S3 withheld a segment.
   *
   * `RetractFrame.reason` is machine vocabulary — `gate_failure:advice_language`,
   * `freshness_contradiction:512.40` — written for an operator auditing a
   * turn, and it was reaching an investor's screen verbatim at the exact
   * moment the system withheld something from them. It leaks how the
   * guardrail is implemented and reads as a crash. The reason still travels
   * in the frame; this is what is drawn instead.
   *
   * Shaped to the spec's refusal pattern: name what happened, reason once,
   * hand back something usable, and never offer a way to the withheld text.
   */
  withheld: string
  /**
   * The mascot screen's heading: what this surface is for, said once, before
   * anything has been asked.
   *
   * Replaces the rail's implicit answer to the same question. The rail said
   * it by showing a sector chart; this says it in words, because the chart
   * left for the Portfolio screen and the panel would otherwise open blank.
   */
  emptyTitle: string
  /** The kicker above the empty state's headline. A product name, not prose. */
  emptyKicker: string
  /**
   * The buttons offered on the mascot screen, tapped rather than retyped.
   *
   * The learning prompts plus the portfolio sector question, which has a
   * verified donut answer. "What is a limit order?" remains absent until
   * the knowledge base can answer it.
   */
  /**
   * The mic, and the four things it can say.
   *
   * There is deliberately no string for a browser that records none of
   * `CONTAINERS`: the button is not drawn at all there, which says the same
   * thing without asking anyone to read it. A stale token reuses
   * `sessionExpired`, because that is what it is.
   */
  mic: string
  micStop: string
  micCancel: string
  micReplay: string
  micPause: string
  showText: string
  hideText: string
  /**
   * Between the answer landing and its voice arriving.
   *
   * Voice out is not instant and on local weights is not close: a
   * 233-character Devanagari answer took 106 seconds to synthesise, and the
   * turn before it hit `Timeouts.speech_s` and never came back at all. Both
   * looked identical on screen, because the answer simply sat there as text —
   * so a feature that was working read as one that had never been built.
   *
   * Naming the wait is the whole of the fix. It says nothing about the
   * outcome, because the outcome is not known yet and every failure here is
   * still silence: the line goes, the words stay, and nothing claims an error
   * the reader cannot act on.
   */
  answerSpeaking: string
  micListening: string
  /**
   * Between the recording stopping and the clip being ready to send.
   *
   * It said "Writing that down" while the transcript landed in the composer,
   * which was exactly what happened and exactly what the reader then saw. The
   * transcript is not shown any more — the question is sent as the recording —
   * so that wording named an outcome nobody ever gets. This names the wait
   * instead, which is the only thing the reader is actually waiting on.
   */
  micWorking: string
  /**
   * The half second the microphone stays open after the reader stops, so the
   * last syllable is kept. Discard is still offered beside it.
   */
  micFinishing: string
  micDenied: string
  micSilent: string
  micUnavailable: string
  micNoMic: string
  spokenQuestion: string
  micInsecure: string
  micShort: string
  micUnreachable: string
  micListeningTap: string
  /**
   * The sr-only half of a delta's direction.
   *
   * `_measure()` serialises with `str(Decimal)`, so a rise arrives as "6.50"
   * and never "+6.50": printing a plus would be typing a character the engine
   * did not send. Colour, an arrow and this word carry it instead, and this
   * word is the one that survives a reader who cannot separate the two hues.
   */
  deltaUp: string
  deltaDown: string
  /**
   * What a citation chip is, for a reader who gets only its label.
   *
   * The chip shows a publisher's hostname; a bare domain announced on its own
   * does not say what it is.
   */
  passageSource: string
  /** Marks a notice that carries no date of any kind, so its position in the
   *  list is not a claim about when it happened. 2,425 of 10,132 company
   *  announcements are in that state. */
  passageUndated: string
  /**
   * The name of the whole book, on the card that draws it.
   *
   * "Your portfolio" and not "Total": the figure beneath it is a total, and
   * a card whose heading and whose figure say the same word says one thing
   * twice.
   */
  portfolioTotal: string
  /**
   * How many positions those totals range over. `{count}` is the engine's
   * own `positions` field, so the numeral is a figure rather than one baked
   * into a label — the distinction this module's no-digits rule is about.
   */
  positionsHeld: string
  /**
   * Holdings the market feed could not price, stated rather than dropped.
   *
   * `get_holdings` scopes its totals to priced positions because a delisted
   * issuer, a debenture or a fund does not price. A total that quietly
   * leaves rows out is the shape of a wrong figure that looks perfectly
   * sourced, so the card says how many.
   */
  unpricedHoldings: string
  /**
   * The attach control beside the mic, and the whole handling of a build or
   * a host that has not granted file access: see `Composer`'s `upload?`
   * prop, the same optional-capability shape `voice?` uses.
   */
  attach: string
  /** The staged-file chip's remove control -- discards it before it is sent. */
  removeAttachment: string
  /**
   * The staged-file chip's own text: what was attached, before it is sent.
   *
   * `{name}` and `{size}` are the browser's own `File.name`/`File.size`, read
   * locally rather than reported by a tool -- not a figure under rule 2, the
   * same ground `TransferBar`'s byte counts stand on.
   */
  attachedFile: string
  /** The header's market pill: its accessible name. */
  marketPill: string
  /** The market pill's four sessions, from `market_status`. */
  marketOpen: string
  marketPreOpen: string
  marketClosed: string
  marketHoliday: string
  /** The pill's countdown, ticked locally from the server's next open or close. */
  marketOpensIn: string
  marketClosesIn: string
  durationHoursMinutes: string
  durationMinutes: string
  /** A ticker's quick sheet (finance UX, B). */
  sheetClose: string
  sheetLoading: string
  sheetFailed: string
  sheetIndex: string
  sheetUnknown: string
  sheetPrice: string
  sheetHistory: string
  sheetDayRange: string
  sheetYearRange: string
  sheetLow: string
  sheetHigh: string
  sheetEps: string
  sheetTurnover: string
  sheetHeld: string
  sheetAsOf: string
  sheetAsk: string
  /** What "Ask about" puts in the composer: output wording, never sent unasked. Swept: a change here must be made in `sweptAsks.json` too. */
  sheetAskQuestion: string
  sheetWatch: string
  sheetCompare: string
  /** The glossary popover, fed by the `term` surface. */
  termOpen: string
  termCourse: string
  termReadMore: string
  termFailed: string
  /** The compare tray: up to three tickers, then one templated question. */
  trayLabel: string
  trayCompare: string
  trayRemove: string
  trayClear: string
  trayFull: string
  trayAdded: string
  trayQuestion: string
  trayAnd: string
  /** Hold-to-talk, and the editable transcript it leaves. */
  micHold: string
  micCheck: string
  heardPick: string
  openSignalZone: string
  /** Under every Analytics Studio turn (`done.handoff`, 2026-10-20): the dashboard concerned, or the list. */
  openAnalyticsStudio: string
  /** Under a Studio turn about a widget that exists (`done.links`, 2026-10-30): the Studio at
   *  that widget, `{title}` its title as the board shows it. */
  openStudioWidget: string
  /** Under a Studio answer that names a dashboard of the reader's (`done.links`
   *  `dashboard_name`, 2026-10-30): that dashboard, `{name}` as their board shows it. */
  openStudioDashboard: string
  /** The portal page that widget's figure lives on: one company's page (`{symbol}`), the
   *  reader's Portfolio, the Market, its Sectors, Brokers, a watchlist. */
  openStockPage: string
  openPortfolio: string
  openMarket: string
  openSectors: string
  openBrokers: string
  openWatchlist: string
  /** A link to Portfolio's Activity, the reader's transactions. */
  openTransactions: string
  /** Any page of this app an answer's data lives on (`done.links` `page`,
   *  2026-11-04), `{label}` the page's name as the engine sent it ("Baskets"). */
  openAppPage: string
  heardNone: string
  heardUndo: string
  heardCheckNumber: string
  micTryAgain: string
  /** Share as image, with the stamp and the not-advice line burned in. */
  answerShare: string
  answerShared: string
  answerShareFailed: string
  shareStamp: string
  shareNotAdvice: string
  shareTitle: string
  /** Pin, and the board the pins live on. */
  answerPin: string
  answerPinned: string
  answerPinFull: string
  answerPinFailed: string
  boardTitle: string
  boardOpen: string
  boardEmpty: string
  boardRemove: string
  boardRemoved: string
  boardUnavailable: string
  boardNotDrawn: string
  boardReadFailed: string
  boardFailed: string
  undo: string
  /** Alerts, Watch and Remind me, confirmed in chat (2026-10-13). */
  sheetAlert: string
  sheetAlertDraft: string
  watchQuestion: string
  watchSymbol: string
  remindQuestion: string
  remindQuestionTitle: string
  remindBookClosure: string
  remindAgm: string
  remindRightShare: string
  remindIpo: string
  /**
   * The Undo toast's words while a Confirm waits behind it. Nothing has been
   * written yet, so each says the act is under way, never that it is done:
   * "set" and "added" are the card's, and only once the commit answered 200.
   */
  confirmAlertPending: string
  confirmWatchPending: string
  confirmWatchChangePending: string
  confirmReminderPending: string
  confirmDashboardPending: string
  /** A model-free Remind me or Watch while its card is asked for, and where it could not be. */
  proposalAsking: string
  proposalFailed: string
  remindersTitle: string
  remindersOpen: string
  remindersEmpty: string
  remindersFailed: string
  remindersDelete: string
  remindersDeleted: string
  reminderEventOn: string
  reminderRemindOn: string
  reminderOnTime: string
  reminderMoved: string
  reminderCancelled: string
  todayTitle: string
  todayDismiss: string
  todayAsk: string
  todayAskQuestion: string
  todayDue: string
  /** A pin's name on the board, from its read key alone. */
  pinQuote: string
  pinFundamentals: string
  pinChart: string
  pinDividends: string
  pinIndex: string
  pinHoldings: string
  pinAllocation: string
  pinPerformance: string
  pinEntitlements: string
  pinMarket: string
  /** The market's sectors' moves (`sectors`), and two or three companies side by side (`compare:`). */
  pinSectors: string
  pinCompare: string
  pinCompareFundamentals: string
  /** The history drawer: the reader's own threads, searched by question. */
  historyTitle: string
  /** The header's control that starts a fresh conversation. */
  newChat: string
  historyOpen: string
  historySearch: string
  historyEmpty: string
  historyNoMatch: string
  historyHidden: string
  historyCard: string
  historyFailed: string
  /** A conversation picked from History or Recent chats that could not be opened in the chat. */
  historyOpenFailed: string
  /**
   * Deleting from History (owner, 2026-10-30): one chat by its trash icon, or
   * every chat at once, each behind a confirm. The engine only hides them
   * from the reader; the conversations stay stored.
   */
  historyDelete: string
  historyDeleteTitle: string
  historyDeleteBody: string
  historyDeleteConfirm: string
  historyClearAll: string
  historyClearTitle: string
  historyClearConfirm: string
  historyConfirmCancel: string
  historyDeleteFailed: string
  /** Thumbs under an answer, and the reasons a down may carry. */
  feedbackUp: string
  feedbackDown: string
  feedbackThanks: string
  feedbackWhy: string
  feedbackWrongFigure: string
  feedbackDidNotAnswer: string
  feedbackTooLong: string
  feedbackWrongLanguage: string
  feedbackOther: string
  /**
   * The panel's two screen-reader announcements (2026-10-13): said politely
   * when a question is sent and when its answer has finished, and nothing
   * else. The streamed words sit in no live region at all.
   */
  srAnswering: string
  srAnswerReady: string
  /** The answer menu: right-click, long-press, or the More button in the row. */
  answerMore: string
  answerMenu: string
  answerAskAbout: string
  /** The low-quota line, from the done frame's `quota_remaining`, at three or fewer. */
  quotaLeftOne: string
  quotaLeftMany: string
  quotaLeftNone: string
  /** An "Ask Mitra" entry point's company scope, as a chip above the composer. */
  scopeAbout: string
  scopeRemove: string
  /** What an entry point puts in the composer (never sent). Swept: a change here must be made in `sweptAsks.json` too. */
  askAboutMarket: string
  askAboutNews: string
  /** The quick sheet's way to the company's own page. */
  sheetOpen: string
  /** Under the compare tray: the answer is a Compare card, which picks nothing. */
  prompts: readonly string[]
  /**
   * The Stage (stage foundation design §3.2): section titles keyed by kind,
   * archetype names, and the page's own controls. Drafted 2026-10-18.
   */
  stageKindPrice: string
  stageKindHolding: string
  stageKindCalendar: string
  stageKindFigures: string
  stageKindBreadth: string
  stageKindSectors: string
  stageKindRing: string
  stageKindTable: string
  stageKindBars: string
  stageKindDefinition: string
  stageKindCompare: string
  stageKindQuiz: string
  stageDuel: string
  /** The duel's trend, as a table and back; the chart's own alternative view. */
  chartAsTable: string
  chartAsChart: string
  chartDate: string
  /** The duel's window choice (1M, 3M, 1Y, 5Y are the server's labels). */
  duelWindow: string
  /** The duel's picker: its three sources, its search field and its close. */
  duelHoldings: string
  duelWatchlists: string
  duelSearch: string
  duelSearchHint: string
  duelClose: string
  stageKindExplain: string
  stageCompany: string
  stageMarketToday: string
  stagePortfolio: string
  stageSectorCompare: string
  stageEvents: string
  stageLearn: string
  stageAnswer: string
  stageOpen: string
  stageShowing: string
  /** The same button while the page covers the thread (a phone). */
  stageShowingLayer: string
  /** The freshness stamp's plain words; `{date}` is the server's date. */
  stampStale: string
  stampFromFiling: string
  stageBack: string
  stageExpand: string
  stageShrink: string
  stageFromApp: string
  stageLaying: string
  stagePlainChart: string
  stageRidgeChart: string
  stageMore: string
  /** The ridge's dashed line at the reader's own average cost per share. */
  stageAverageCost: string
  /**
   * A section's proof (Pramaan): the heading control's visible word and its
   * accessible name, the sheet's header, its row labels, and its two fixed
   * values. Mobile's ARB `mitraStagePramaan*`, word for word (task 8 rulings).
   * The accessible name starts with the visible word, so the name a speech
   * user says is the name the control answers to (WCAG 2.5.3).
   */
  stagePramaanOpen: string
  stagePramaanWhere: string
  stagePramaanTitle: string
  stagePramaanWhat: string
  stagePramaanAsOf: string
  stagePramaanState: string
  stagePramaanPeriod: string
  stagePramaanSource: string
  stagePramaanSourceMarket: string
  stagePramaanSourcePortfolio: string
  // Interactive shapes (2026-10-18). No digits: a figure is the server's.
  /** A button under a playable card that opens its controls. */
  playTryYours: string
  /** The legend on a card the reader has moved away from Mitra's numbers. */
  playYours: string
  /** Shown while the reader's own numbers are on the card: the answer's words are about Mitra's. */
  playStale: string
  /** Returns a played card to the numbers as sent. */
  playReset: string
  /** Asks Mitra about the played card's current stop. */
  playAsk: string
  /** The prompt to type a value into a control with bounds. */
  playEnter: string
  /** Legend: the card shows a premise the reader chose. */
  playLegendPremise: string
  /** Legend: the card is arithmetic at an entered rate, not a forecast. */
  playLegendEstimate: string
  /** Legend: the card replays past closes. */
  playLegendHistory: string
  /** Legend: the card previews a sale nobody made. */
  playLegendSale: string
  /** Label for stepping through a story card's stops. */
  playStepThrough: string
  /** Label for comparing two cards. */
  playSideBySide: string
  /** A control's name; {symbol} is the company. */
  playChange: string
  /** A duel's note; {a} and {b} are the two companies. */
  playWroteAbout: string
  /** A duel's ask; {a} and {b} are the two companies. */
  playAskAbout: string
  /** Predict-first: invites a guess before the figure shows. */
  playGuessPrompt: string
  /** Predict-first: skips the guess. */
  playGuessSkip: string
  /** Predict-first: labels the reader's guess. */
  playGuessYours: string
  /** Predict-first: labels the figure the data gave. */
  playGuessReal: string
  /** Shown when an entered value is outside the bounds or unusable. */
  playEnterInvalid: string
  /** Names of a played card's controls. */
  playControlMove: string
  playControlQuantity: string
  playControlPrice: string
  playControlMonthly: string
  playControlYears: string
  playControlStart: string
  playControlRate: string
  /** The entry-only rate's field name (Task 8). */
  playRate: string
  playTarget: string
  /** The guess step's confirm button. */
  playGuessLock: string
  /** Shown where a start has no cell: there is no figure to set the guess beside. */
  playGuessNone: string
  /** The question the swap button composes. */
  playAskPairQuestion: string
  /** Sends a typed value from a play control's entry field. */
  playEnterUse: string
  /** Names the thin line shown while a played card's request is in flight. */
  playPending: string
  /** The what-if's choice between one holding and every holding, and its holding switch. */
  playControlScope: string
  playControlHolding: string
  /** Under a played card whose newest request failed (a limit, an outage): the card is back on its last answer. */
  playFailed: string
  /** Under a duel whose company swap failed: the page still shows the pair it showed. */
  playSwapFailed: string
  /** A story's step caption; {n}, {total} and {title} are filled in. */
  storyStep: string
  /** Opens the whole page from a story. */
  storySeeFull: string
  /** Names a story's step rail. */
  storySteps: string
  /** The story's visible place; {n} and {total} are filled in. */
  storyCount: string
  /** Moves a story on one step. */
  storyNext: string
  /** Moves a story back one step. */
  storyPrevious: string
  /** Under a beginner's answer, what opens the defined term's full sheet; {term} is filled in. */
  termBriefMore: string
}

const EN: Copy = {
  stageKindPrice: 'Price',
  stageKindHolding: 'Your holding',
  stageKindCalendar: 'Dates ahead',
  stageKindFigures: 'Key figures',
  stageKindBreadth: 'Up and down today',
  stageKindSectors: 'Sectors',
  stageKindRing: 'How your shares are spread',
  stageKindTable: 'In detail',
  stageKindBars: 'Side by side',
  stageKindDefinition: 'What it means',
  stageKindCompare: 'Side by side',
  stageKindQuiz: 'Check yourself',
  stageDuel: 'Two companies',
  chartAsTable: 'Show as table',
  chartAsChart: 'Show as chart',
  chartDate: 'Date',
  duelWindow: 'Period',
  duelHoldings: 'Your holdings',
  duelWatchlists: 'Your watchlists',
  duelSearch: 'Search companies',
  duelSearchHint: 'Type a name or symbol',
  duelClose: 'Close',
  stageKindExplain: 'Mitra explains',
  stageCompany: 'Company deep-dive',
  stageMarketToday: 'Market today',
  stagePortfolio: 'Your portfolio',
  stageSectorCompare: 'Compared',
  stageEvents: 'Events ahead',
  stageLearn: 'Learn the idea',
  stageAnswer: "Mitra's answer",
  stageOpen: 'Open full page',
  stageShowing: 'Open beside this chat',
  stageShowingLayer: 'Showing',
  stageBack: 'Back to chat',
  stageExpand: 'Full width',
  stageShrink: 'Side view',
  stampStale: 'Last updated {date}',
  stampFromFiling: 'From filing',
  stageFromApp: 'From the app',
  stageLaying: 'Laying out the page',
  stagePlainChart: 'Plain chart',
  stageRidgeChart: 'Ridge view',
  stageMore: 'More from this answer',
  stageAverageCost: 'Your average cost',
  stagePramaanOpen: 'Proof',
  stagePramaanWhere: 'Proof: where this is from',
  stagePramaanTitle: 'Proof',
  stagePramaanWhat: 'What',
  stagePramaanAsOf: 'As of',
  stagePramaanState: 'State',
  stagePramaanPeriod: 'Period',
  stagePramaanSource: 'Where it came from',
  stagePramaanSourceMarket: 'Exchange and company data read by MoneyMitra',
  stagePramaanSourcePortfolio: 'Your portfolio record in MoneyMitra',
  ask: 'Ask me anything…',
  askFollowUp: 'Ask a follow-up',
  mic: 'Speak',
  micStop: 'Stop',
  micCancel: 'Discard',
  micReplay: 'Hear it back',
  micPause: 'Pause',
  showText: 'Read it',
  hideText: 'Hide text',
  // Deliberately not "Reading it out": `showText` above is "Read it", and two
  // controls a tap apart must not describe the same act in the same words.
  // This names the wait the way `micWorking` does, which is the wording that
  // survived the last time a caption promised an outcome instead.
  answerSpeaking: 'Getting the voice ready',
  micListening: 'Listening',
  micWorking: 'Getting it ready',
  micFinishing: 'Finishing…',
  micDenied:
    'Mitra cannot use the microphone. Allow it for this site, then try again: in Chrome, Edge or Samsung Internet, the icon beside the address; in Firefox, the microphone icon in the address bar; on an iPhone, Settings › Safari › Microphone.',
  micSilent: 'I did not hear anything. Try again, or type instead.',
  micUnavailable: 'This browser cannot record here. You can type instead.',
  micNoMic: 'No microphone was found. Connect one, or type instead.',
  spokenQuestion: 'Spoken',
  micInsecure: 'Voice needs a secure (https) page. You can type instead.',
  micShort: 'Hold the mic while you speak, or tap it once to start and again to stop.',
  micUnreachable: 'Mitra could not be reached. Try again, or type instead.',
  micListeningTap: 'Listening · tap the mic again to stop',
  deltaUp: 'up',
  deltaDown: 'down',
  passageSource: 'Source',
  passageUndated: 'no date given',
  portfolioTotal: 'Your portfolio',
  positionsHeld: '{count} positions',
  unpricedHoldings: '{count} more could not be priced',
  send: 'Send',
  stopAnswer: 'Stop',
  factsLow: 'Low',
  factsHigh: 'High',
  factsTodaysRange: 'Today’s range',
  factsTradingRanges: 'Trading ranges',
  factsIndex: 'Index',
  groupMedian: 'MEDIAN',
  groupAverage: 'AVG',
  jumpToLatest: 'Jump to the latest',
  whatIfHint: 'Release the slider or press Enter to ask.',
  whatIfAsk: 'Ask',
  askNext: 'Ask next',
  suggestTry: 'Try asking',
  suggestMore: 'More suggestions',
  deskMarket: "Today's market",
  deskAskMarket: 'How did the market do today?',
  deskBreadth: '{up} up, {down} down',
  deskBook: 'Your portfolio',
  deskBookToday: 'today',
  deskAskBook: 'How has my portfolio done today?',
  deskAddHoldings: 'Add your holdings',
  deskCompanies: 'Your companies',
  deskLearn: 'Learn',
  deskRemindersBoard: 'Reminders · Board',
  deskPinned: '{n} pinned',
  deskNoReminder: 'Nothing due today',
  deskContinue: 'Continue: “{question}”',
  deskStrip: 'Market today',
  deskTry: 'Try “{question}”',
  deskCompaniesExplore: 'Companies to explore',
  deskOpenCompany: '{name}, open company',
  markUp: 'up',
  markDown: 'down',
  markOpensCompany: '{name}, shows details',
  markOpensPage: '{name}, opens {page}',
  markPageIndex: 'indices',
  markPageSector: 'sectors',
  markPageBroker: 'broker',
  markPageIpo: 'IPO',
  markPageTerm: 'dictionary',
  markPageSignalZone: 'Signal Zone',
  leadUp: 'Price up',
  leadDown: 'Price down',
  leadDates: 'Dates',
  leadLearn: 'Learn',
  deskLearnTap: 'Tap to learn',
  deskUpcoming: 'Upcoming for your shares',
  deskPinHint: 'Pin an answer to keep it here',
  deskRemindHint: 'Set a reminder from any book-closure card',
  deskReminderCount: 'Reminders · {n}',
  deskEventBookClosure: 'Book closure',
  deskEventAgm: 'AGM',
  deskEventRight: 'Rights issue',
  deskEventIpo: 'IPO',
  deskPrevClose: 'Prev. close {value}',
  whyItMoved: 'Why it moved',
  deskExamples: [
    'Try “What is NABIL trading at?”',
    'Try “How is my portfolio doing?”',
    'Try “What does book close mean?”',
  ],
  answerActions: 'Answer actions',
  answerCopy: 'Copy',
  answerCopied: 'Copied',
  answerCopyFailed: 'Could not copy',
  answerListen: 'Listen',
  answerListenStop: 'Stop',
  answerVoiceFailed: 'The voice is not available right now',
  attach: 'Attach a statement',
  removeAttachment: 'Remove',
  attachedFile: '{name} · {size}',
  reading: 'reading your portfolio',
  uploading: 'sending your statement',
  phaseThinking: 'reading your question',
  phaseMarket: 'reading NEPSE data',
  phaseNews: 'reading the news',
  phaseRules: 'reading the rules',
  phaseFigures: 'working out the figures',
  phaseWorking: 'working on it',
  phaseWriting: 'writing the answer',
  phaseVoice: "writing it in Mitra's voice",
  phaseChecking: 'double-checking the figures',
  loading: 'Reading your portfolio…',
  answerFailed:
    'I cannot answer that right now. This is my end — what is already on screen is still yours.',
  answerIncomplete:
    'Answer incomplete — the turn stopped before it finished. Ask again and I will pick it up.',
  disclosureTitle: 'Before we start',
  // THE FIRST THING A VISITOR READS, so it may only say what somebody has
  // signed. Until 2026-09-10 its last sentence was "Licensed calls live on the
  // Signals tab", which asserts on Signal Zone's behalf that it holds a
  // licence to make buy and sell calls -- Signal Zone's own in-app banner
  // denies holding one, and the engine spent four commits taking the same
  // claim out of `SIGNALS_CARD`, the withheld row, the system prefix and the
  // knowledge base while this copy went on making it to every visitor before
  // their first question, in all three registers.
  //
  // The first two sentences stay: what this assistant does NOT do is its own
  // position about itself, needs nobody's signature, and is the point of a
  // disclosure. The third, a pointer to Signal Zone, is gone (owner,
  // 2026-09-30): the app has no such surface. `signalZoneClaim.test.ts`
  // refuses the claim across every register and every embed string, rather
  // than here alone, because one-surface-at-a-time is how the engine's copy
  // survived three fixes.
  disclosureBody:
    "I read your portfolio and NEPSE's own data. I describe what is there — " +
    'I do not recommend what to buy or sell.',
  disclosureAccept: 'Got it',
  colourBlind: 'Colour-blind friendly',
  colourBlindTitle: 'Colour-blind',
  colourBlindNote: 'Gains in blue, losses in orange',
  sourceYourPortfolio: 'your portfolio',
  headerMore: 'More options',
  headerNote: 'AI · reads your portfolio',
  unreachable:
    'I cannot read your holdings right now. Nothing is wrong with your portfolio — this is my end.',
  noHoldings: 'You have no priced holdings for me to weigh against NEPSE.',
  nothingHere: 'Nothing to show on this page yet.',
  sessionExpired: 'Your session ended. Reload the page and I will pick this up.',
  engineSignInFailed:
    'Mitra could not check your sign-in just now. This is on our side, not yours. ' +
    'Please try again later.',
  notInRollout: 'Mitra is not open on your account yet.',
  engineAway: 'I can’t answer just now. Your question is kept, so try again shortly.',
  engineAwayIdle: 'I can’t answer just now. Try again shortly.',
  workedThrough: 'Worked through {n} steps in {s} s',
  workedThroughOne: 'Worked through one step in {s} s',
  sessionMissing:
    'I was never signed in to your account on this page, so I cannot answer ' +
    'anything about your holdings. Nothing is wrong with your portfolio — this is my end.',
  newest: 'Newest',
  conversationLabel: 'Conversation with Mitra',
  youAsked: 'You asked',
  tagMitra: 'Mitra',
  tagPortfolio: 'Portfolio',
  tagCompany: 'Company',
  tagMarket: 'Market',
  tagScreen: 'Screen',
  tagTax: 'Tax',
  tagPlan: 'Plan',
  tagLearn: 'Learn',
  tagFiles: 'Your files',
  sourcesCheckedOne: 'Checked {n} source',
  sourcesCheckedMany: 'Checked {n} sources',
  sourcesFailed: ', {n} failed',
  sourcesHide: 'Hide',
  replyThinking:
    "Okay, let's think about this for a sec…|Good one… let's take a look|Alright, no delay, let's get into it…",
  replyStepFigures:
    "Let's line up the numbers one by one…|So how much is it, really? Working it out…",
  replyStepChecking:
    "Can't tell you anything wrong, so checking once more…|What does the data actually say? Checking again…",
  replyStepWords: "Now let's put this in simple words…|How do I say this simply? Thinking…",
  replyStepAlmost: 'Okay, almost there…|Just a sec, nearly done…',
  replyChatStart: 'Okay, one sec…|Let me think…',
  replyChatWords: 'Putting it into words…|Just a moment…',
  replyWithheld: 'I took that answer back because I could not confirm all of it. Please ask again.',
  secondsElapsed: '{n}s',
  mitraSaid: 'Mitra',
  closingPrice: 'Closing price',
  filingStamp: 'FILING',
  marketAdvanced: 'Advanced',
  marketDeclined: 'Declined',
  marketUnchanged: 'Unchanged',
  contextPortfolio: 'Your portfolio',
  refused: 'Refused',
  widen: 'Widen the panel',
  narrow: 'Narrow the panel',
  close: 'Close the panel',
  showingOf: 'Showing {shown} of {total} rows',
  showAll: 'Show all {total}',
  withheld:
    'I stopped that answer before it finished \u2014 it did not clear the checks I hold ' +
    'myself to. Ask again and I will keep to what your holdings show.',
  // "What do you want to know?" per the live empty-state frame (`5451:103666`,
  // 2026-09-28 design refresh) — replaces the earlier "Ask about Nepal's
  // market" guess.
  emptyTitle: 'What do you want to know?',
  emptyKicker: 'Mitra AI',
  // Buttons, not prose to retype. In Nepali that difference is the
  // difference between asking and not bothering.
  srAnswering: 'Mitra is answering',
  srAnswerReady: 'Answer ready',
  answerMore: 'More',
  answerMenu: 'Answer options',
  answerAskAbout: 'Ask about this',
  quotaLeftOne: '{n} question left today',
  quotaLeftMany: '{n} questions left today',
  quotaLeftNone: 'No questions left today',
  scopeAbout: 'About {symbol}',
  scopeRemove: 'Remove {symbol} from this question',
  askAboutMarket: 'How did the market move today?',
  askAboutNews: 'Tell me about this news: {headline}',
  sheetOpen: 'Open {symbol} page',
  prompts: [
    'How is my portfolio allocated by sector?',
    'What is a kitta?',
    'How does an IPO application work?',
    'Why did a price stop moving?',
  ],
  marketPill: 'NEPSE market',
  marketOpen: 'Open',
  marketPreOpen: 'Pre-open',
  marketClosed: 'Closed',
  marketHoliday: 'Holiday',
  marketOpensIn: 'opens in {time}',
  marketClosesIn: 'closes in {time}',
  durationHoursMinutes: '{h}h {m}m',
  durationMinutes: '{m}m',
  sheetClose: 'Close',
  sheetLoading: 'Loading…',
  sheetFailed: "Couldn't load this right now.",
  sheetIndex: 'This is an index. Quick facts are for listed companies.',
  sheetUnknown: 'No listed company goes by this symbol.',
  sheetPrice: 'Last price',
  sheetHistory: 'Price history',
  sheetDayRange: "Day's range",
  sheetYearRange: 'Year range',
  sheetLow: 'Low',
  sheetHigh: 'High',
  sheetEps: 'EPS',
  sheetTurnover: 'Turnover',
  sheetHeld: 'You hold {kitta} kitta',
  sheetAsOf: 'As of {date}',
  sheetAsk: 'Ask about {symbol}',
  sheetAskQuestion: 'Tell me about {symbol}.',
  sheetWatch: 'Watch',
  sheetCompare: 'Compare',
  termOpen: 'What {term} means',
  termCourse: 'From the course: {title}',
  termReadMore: 'Read more',
  termFailed: "Couldn't load this term.",
  trayLabel: 'Compare tray',
  trayCompare: 'Ask Mitra to compare',
  trayRemove: 'Remove {symbol}',
  trayClear: 'Clear',
  trayFull: 'The tray holds three.',
  trayAdded: '{symbol} added to compare',
  trayQuestion: 'Compare {symbols} side by side.',
  trayAnd: 'and',
  micHold: 'Tap or hold to talk',
  micCheck: 'Check the words, then send',
  heardPick: 'Did you mean',
  openSignalZone: 'Open Signal Zone',
  openAnalyticsStudio: 'Open Analytics Studio',
  openStudioWidget: 'Open the {title} widget',
  openStudioDashboard: 'Open {name}',
  openStockPage: 'Open {symbol}',
  openPortfolio: 'Open Portfolio',
  openMarket: 'Open Market',
  openSectors: 'Open Sectors',
  openBrokers: 'Open Brokers',
  openWatchlist: 'Open Watchlist',
  openTransactions: 'Open Activity',
  openAppPage: 'Open {label}',
  heardNone: 'None of these',
  heardUndo: 'Use what you said: {heard}',
  heardCheckNumber: 'Check this number',
  micTryAgain: 'Try again',
  answerShare: 'Share',
  answerShared: 'Image ready',
  answerShareFailed: "Couldn't make the image",
  shareStamp: 'From Mitra, MoneyMitra · {date}',
  shareNotAdvice: 'Information, not advice',
  shareTitle: 'An answer from Mitra',
  answerPin: 'Pin',
  answerPinned: 'Pinned to your board',
  answerPinFull: 'Your board is full',
  answerPinFailed: "Couldn't pin this",
  boardTitle: 'My board',
  boardOpen: 'Open my board',
  boardEmpty: 'Nothing pinned yet. Pin an answer to see its figures fresh here.',
  boardRemove: 'Remove',
  boardRemoved: 'Removed from your board',
  boardUnavailable: 'This read is no longer available.',
  boardNotDrawn: 'Nothing to show for this read right now.',
  boardReadFailed: "Couldn't refresh this right now.",
  boardFailed: "Couldn't open your board.",
  undo: 'Undo',
  sheetAlert: 'Alert',
  sheetAlertDraft: 'Set an alert for {symbol} at Rs ',
  watchQuestion: 'Add {symbol} to my watchlist',
  watchSymbol: 'Add {symbol} to your watchlist',
  remindQuestion: 'Remind me about the {event} for {symbol}',
  remindQuestionTitle: 'Remind me about {title}',
  remindBookClosure: 'book closure',
  remindAgm: 'AGM',
  remindRightShare: 'right share',
  remindIpo: 'IPO',
  confirmAlertPending: 'Setting alert…',
  confirmWatchPending: 'Adding to your watchlist…',
  confirmWatchChangePending: 'Saving your watchlist change…',
  confirmReminderPending: 'Setting reminder…',
  confirmDashboardPending: 'Saving to Analytics Studio…',
  proposalAsking: 'Preparing it…',
  proposalFailed: "That couldn't be prepared just now.",
  remindersTitle: 'Reminders',
  remindersOpen: 'Open reminders',
  remindersEmpty:
    'No reminders yet. Tap Remind me on a book closure, AGM, right share or IPO to add one.',
  remindersFailed: "Couldn't load your reminders.",
  remindersDelete: 'Delete',
  remindersDeleted: 'Reminder deleted',
  reminderEventOn: 'Event {date}',
  reminderRemindOn: 'Reminds you {date}',
  reminderOnTime: 'On schedule',
  reminderMoved: 'Date moved',
  reminderCancelled: 'Cancelled',
  todayTitle: 'Today',
  todayDismiss: 'Dismiss',
  todayAsk: 'Ask about it',
  todayAskQuestion: 'Tell me about {title}.',
  todayDue: 'A reminder is due today',
  pinQuote: '{symbol} price',
  pinFundamentals: '{symbol} fundamentals',
  pinChart: '{symbol} price history',
  pinDividends: '{symbol} dividends',
  pinIndex: '{symbol} index',
  pinHoldings: 'Your holdings',
  pinAllocation: 'Your sector allocation',
  pinPerformance: 'Your portfolio performance',
  pinEntitlements: 'Your book closures',
  pinMarket: 'Market snapshot',
  pinSectors: 'Sector moves',
  pinCompare: 'Compare {symbols}',
  pinCompareFundamentals: 'Compare fundamentals {symbols}',
  historyTitle: 'History',
  newChat: 'New chat',
  historyOpen: 'Open history',
  historySearch: 'Search your questions',
  historyEmpty: 'No conversations yet.',
  historyNoMatch: 'Nothing matches that.',
  historyHidden: "This conversation's content is hidden.",
  historyCard: 'This answer had a card.',
  historyFailed: "Couldn't load your history.",
  historyOpenFailed: "Couldn't open that chat. Try again.",
  historyDelete: 'Delete this chat',
  historyDeleteTitle: 'Delete this chat?',
  historyDeleteBody: 'It will be removed from your history.',
  historyDeleteConfirm: 'Delete',
  historyClearAll: 'Clear all history',
  historyClearTitle: 'Clear all chats from your history?',
  historyClearConfirm: 'Clear all',
  historyConfirmCancel: 'Cancel',
  historyDeleteFailed: "Couldn't delete that. Try again.",
  feedbackUp: 'Helpful',
  feedbackDown: 'Not helpful',
  feedbackThanks: 'Thanks for telling us.',
  feedbackWhy: 'What went wrong?',
  feedbackWrongFigure: 'Wrong figure',
  feedbackDidNotAnswer: "Didn't answer",
  feedbackTooLong: 'Too long',
  feedbackWrongLanguage: 'Wrong language',
  feedbackOther: 'Other',
  playTryYours: 'Try your own numbers',
  playYours: 'Your numbers',
  playStale: "The answer above is for Mitra's numbers. Ask to hear it for yours.",
  playReset: "Back to Mitra's numbers",
  playAsk: 'Ask about this',
  playEnter: 'Enter a number',
  playLegendPremise: 'A change you chose',
  playLegendEstimate: 'A calculation at the rate you entered, not a forecast',
  playLegendHistory: 'Past closes, not what will happen',
  playLegendSale: 'A preview of a sale nobody made',
  playStepThrough: 'Step through',
  playSideBySide: 'Side by side',
  playChange: 'Change {symbol}',
  playWroteAbout: 'Mitra wrote about {a} and {b}',
  playAskAbout: 'Ask about {a} and {b}',
  playGuessPrompt: 'Your guess first',
  playGuessSkip: 'Skip',
  playGuessYours: 'Your guess',
  playGuessReal: 'What it became',
  playEnterInvalid: 'That number cannot be used here',
  playControlMove: 'If it moves',
  playControlQuantity: 'Quantity',
  playControlPrice: 'Price',
  playControlMonthly: 'Each month',
  playControlYears: 'Years',
  playControlStart: 'Starting back',
  playControlRate: 'Growth rate',
  playRate: 'Your rate',
  playTarget: 'Your target',
  playGuessLock: 'Lock in my guess',
  playGuessNone: 'Not available for this start',
  playAskPairQuestion: '{a} or {b}, side by side',
  playEnterUse: 'Use this number',
  playPending: 'Working out your numbers',
  playControlScope: 'Which holdings',
  playControlHolding: 'Holding',
  playFailed: 'Your numbers could not be worked out just now',
  playSwapFailed: 'That company could not be shown just now',
  storyStep: 'Step {n} of {total}: {title}',
  storySeeFull: 'See full page',
  storySteps: 'Steps',
  storyCount: '{n} of {total}',
  storyNext: 'Next',
  storyPrevious: 'Previous',
  termBriefMore: 'More on {term}',
}

// Devanagari. None of these could be sourced from the lexicons doc (see the
// module docstring) and none was composed here — a machine translation of a
// licensing sentence is a licensing risk nobody reviewed, and the same
// caution applies to the rest of the table for consistency, not just
// disclosureBody. Every key falls back to the English string until a Nepali
// speaker reviews and replaces it.
// TODO(counsel): translate and review every key in this table into
// Devanagari; disclosureBody is the licensing sentence and needs the most
// scrutiny, but all thirty-one keys are currently unsourced English fallbacks.
const NE: Copy = {
  // TODO(speaker review): drafted 2026-10-18 for the Stage, the page an
  // answer opens beside the thread. Section titles and the page's controls;
  // a speaker should read the controls as what a tap does.
  stageKindPrice: 'मूल्य',
  stageKindHolding: 'तपाईंको सेयर',
  stageKindCalendar: 'आउँदा मितिहरू',
  stageKindFigures: 'मुख्य तथ्याङ्क',
  stageKindBreadth: 'आज बढ्ने र घट्ने',
  stageKindSectors: 'क्षेत्रहरू',
  stageKindRing: 'तपाईंको सेयरको बाँडफाँड',
  stageKindTable: 'विस्तारमा',
  stageKindBars: 'छेउछाउ राखेर',
  stageKindDefinition: 'यसको अर्थ',
  // TODO(speaker review): the duel's words, drafted 2026-10-19.
  stageKindCompare: 'छेउछाउ',
  stageKindQuiz: 'आफैँ जाँच्नुहोस्',
  stageDuel: 'दुई कम्पनी',
  chartAsTable: 'तालिकाका रूपमा हेर्नुहोस्',
  chartAsChart: 'चार्टका रूपमा हेर्नुहोस्',
  chartDate: 'मिति',
  duelWindow: 'अवधि',
  duelHoldings: 'तपाईंका सेयर',
  duelWatchlists: 'तपाईंका वाचलिस्ट',
  duelSearch: 'कम्पनी खोज्नुहोस्',
  duelSearchHint: 'नाम वा सङ्केत लेख्नुहोस्',
  duelClose: 'बन्द गर्नुहोस्',
  stageKindExplain: 'मित्र बताउँछ',
  stageCompany: 'कम्पनीको विस्तृत झलक',
  stageMarketToday: 'आजको बजार',
  stagePortfolio: 'तपाईंको पोर्टफोलियो',
  // TODO(speaker review): draft
  stageSectorCompare: 'तुलना',
  stageEvents: 'आउँदा घटनाहरू',
  stageLearn: 'कुरा बुझ्नुहोस्',
  // TODO(speaker review): the general page's name, drafted 2026-10-18.
  stageAnswer: 'मित्रको जवाफ',
  // TODO(speaker review): the plain-words drafts of 2026-10-18.
  stageOpen: 'पूरा पेज खोल्नुहोस्',
  stageShowing: 'यो च्याटको छेउमा खुला छ',
  stageShowingLayer: 'खुला छ',
  stageBack: 'च्याटमा फर्कनुहोस्',
  stageExpand: 'पूरा चौडा',
  stageShrink: 'छेउमा',
  stampStale: 'अन्तिम अपडेट {date}',
  stampFromFiling: 'वित्तीय विवरण',
  stageFromApp: 'एपबाट',
  stageLaying: 'पेज मिलाउँदै छु',
  stagePlainChart: 'साधारण चार्ट',
  stageRidgeChart: 'धर्सा दृश्य',
  stageMore: 'यो उत्तरबाट अरू',
  // TODO(speaker review): drafted 2026-10-18 for the ridge's cost line.
  stageAverageCost: 'तपाईंको औसत लागत',
  // TODO(speaker review): drafted 2026-10-18 for a Stage section's proof
  // (Pramaan), the same words as mobile's `stage_copy.dart`.
  stagePramaanOpen: 'प्रमाण',
  stagePramaanWhere: 'प्रमाण: यो कहाँबाट आयो',
  stagePramaanTitle: 'प्रमाण',
  stagePramaanWhat: 'के',
  stagePramaanAsOf: 'कहिलेसम्म',
  stagePramaanState: 'अवस्था',
  stagePramaanPeriod: 'अवधि',
  stagePramaanSource: 'कहाँबाट आयो',
  stagePramaanSourceMarket: 'मनीमित्रले पढेको एक्सचेन्ज र कम्पनीको तथ्यांक',
  stagePramaanSourcePortfolio: 'मनीमित्रमा रहेको तपाईंको पोर्टफोलियो विवरण',
  ask: 'जे पनि सोध्नुहोस्…',
  // TODO(speaker review): drafted 2026-10-13; the placeholder stayed English
  // on Devanagari turns (13e). A control, so a speaker should read it as one.
  askFollowUp: 'अर्को प्रश्न सोध्नुहोस्…',
  mic: 'Speak',
  micStop: 'Stop',
  micCancel: 'Discard',
  micReplay: 'Hear it back',
  micPause: 'Pause',
  showText: 'Read it',
  hideText: 'Hide text',
  answerSpeaking: 'Getting the voice ready',
  micListening: 'सुन्दै छु',
  micWorking: 'तयार पार्दै छु',
  // TODO(speaker review): drafted 2026-10-14 for the mic's half-second tail.
  micFinishing: 'सक्दै छु…',
  micDenied:
    'मित्रले माइक्रोफोन चलाउन सकेन। ब्राउजरको साइट सेटिङमा, ठेगाना पट्टीको छेउमा, अनुमति दिनुहोस् अनि फेरि प्रयास गर्नुहोस्।',
  micSilent: 'मैले केही सुनिनँ। फेरि प्रयास गर्नुहोस्, वा टाइप गर्नुहोस्।',
  micUnavailable: 'यो ब्राउजरले यहाँ रेकर्ड गर्न सक्दैन। टाइप गर्न सक्नुहुन्छ।',
  // TODO(speaker review): drafted 2026-10-14 for the mic's failures and its
  // tap mode, beside the mic lines above, which were English until now.
  micNoMic: 'माइक्रोफोन भेटिएन। जोड्नुहोस्, वा टाइप गर्नुहोस्।',
  spokenQuestion: 'बोलेर सोधिएको',
  micInsecure: 'आवाजका लागि सुरक्षित (https) पेज चाहिन्छ। टाइप गर्न सक्नुहुन्छ।',
  micShort: 'बोल्दा माइक थिचिराख्नुहोस्, वा सुरु गर्न एकपटक र रोक्न फेरि थिच्नुहोस्।',
  micUnreachable: 'मित्रसम्म पुग्न सकिएन। फेरि प्रयास गर्नुहोस्, वा टाइप गर्नुहोस्।',
  micListeningTap: 'सुन्दै छु · रोक्न माइक फेरि थिच्नुहोस्',
  deltaUp: 'up',
  deltaDown: 'down',
  passageSource: 'Source',
  passageUndated: 'no date given',
  portfolioTotal: 'Your portfolio',
  positionsHeld: '{count} positions',
  unpricedHoldings: '{count} more could not be priced',
  send: 'पठाउनुहोस्',
  stopAnswer: 'रोक्नुहोस्',
  // TODO(speaker review): drafted 2026-10-05 for the NEPSE index card
  // (wow retest 2); labels over figures, worded as `lib/figures.ts` is.
  factsLow: 'न्यून',
  factsHigh: 'उच्च',
  factsTodaysRange: 'आजको दायरा',
  factsTradingRanges: 'कारोबार दायरा',
  factsIndex: 'सूचकाङ्क',
  // TODO(speaker review): drafted 2026-10-05, matching the engine's own
  // tick words (`streaming/captions.py`).
  groupMedian: 'मध्यक',
  groupAverage: 'औसत',
  jumpToLatest: 'Jump to the latest',
  // TODO(speaker review): drafted 2026-10-05 for the what-if slider's hint.
  whatIfHint: 'सोध्न स्लाइडर छोड्नुहोस् वा Enter थिच्नुहोस्।',
  // TODO(speaker review): drafted 2026-10-05 for the what-if slider's Ask
  // button and the label over the next-step chip; a control and an eyebrow,
  // so a speaker should read both as what a tap does.
  whatIfAsk: 'सोध्नुहोस्',
  askNext: 'अर्को प्रश्न',
  // TODO(speaker review): drafted 2026-10-14 for the suggestion list's heading and its More.
  suggestTry: 'यस्तो सोध्नुहोस्',
  suggestMore: 'थप सुझाव',
  // TODO(speaker review): drafted 2026-10-14 for Mitra's Desk, the empty chat's first screen.
  deskMarket: 'आजको बजार',
  deskAskMarket: 'आज बजार कस्तो रह्यो?',
  deskBreadth: '{up} बढे, {down} घटे',
  deskBook: 'मेरो पोर्टफोलियो',
  deskBookToday: 'आज',
  deskAskBook: 'आज मेरो पोर्टफोलियो कस्तो रह्यो?',
  deskAddHoldings: 'आफ्नो सेयर थप्नुहोस्',
  // TODO(speaker review): reworded 2026-10-14, the reader's own companies.
  deskCompanies: 'तपाईंका कम्पनी',
  deskLearn: 'सिक्नुहोस्',
  deskRemindersBoard: 'रिमाइन्डर · बोर्ड',
  deskPinned: '{n} पिन गरिएको',
  deskNoReminder: 'आज केही बाँकी छैन',
  deskContinue: 'जारी राख्नुहोस्: “{question}”',
  deskStrip: 'आजको बजार',
  deskTry: '“{question}” सोध्नुहोस्',
  // TODO(speaker review): drafted 2026-10-14 for Desk v2's tiles.
  deskCompaniesExplore: 'हेर्न लायक कम्पनी',
  deskOpenCompany: '{name}, कम्पनी खोल्नुहोस्',
  // TODO(speaker review): drafted 2026-10-14 for the lead line's mark.
  // TODO(speaker review): drafted 2026-10-14 for answer emphasis.
  markUp: 'बढेको',
  markDown: 'घटेको',
  // TODO(speaker review): reworded 2026-10-14, a tap opens the quick sheet.
  markOpensCompany: '{name}, विवरण देखाउँछ',
  // TODO(speaker review): drafted 2026-10-14 for answer links (wire §13).
  markOpensPage: '{name}, {page} खोल्छ',
  markPageIndex: 'सूचकाङ्कहरू',
  markPageSector: 'क्षेत्रहरू',
  markPageBroker: 'ब्रोकर',
  markPageIpo: 'आईपीओ',
  markPageTerm: 'शब्दकोश',
  markPageSignalZone: 'सिग्नल जोन',
  leadUp: 'भाउ बढेको',
  leadDown: 'भाउ घटेको',
  leadDates: 'मितिहरू',
  leadLearn: 'सिकाइ',
  deskLearnTap: 'सिक्न थिच्नुहोस्',
  deskUpcoming: 'तपाईंका सेयरका आगामी मिति',
  deskPinHint: 'उत्तर पिन गर्नुहोस्, यहीँ रहन्छ',
  deskRemindHint: 'कुनै पनि बुक क्लोज कार्डबाट रिमाइन्डर राख्नुहोस्',
  deskReminderCount: 'रिमाइन्डर · {n}',
  deskEventBookClosure: 'बुक क्लोज',
  deskEventAgm: 'साधारण सभा',
  deskEventRight: 'हकप्रद सेयर',
  deskEventIpo: 'आईपीओ',
  deskPrevClose: 'अघिल्लो बन्द {value}',
  // TODO(speaker review): drafted 2026-10-26 for the why beside a move.
  whyItMoved: 'किन चल्यो',
  deskExamples: [
    '“NABIL को भाउ कति छ?” सोध्नुहोस्',
    '“मेरो पोर्टफोलियो कस्तो छ?” सोध्नुहोस्',
    '“बुक क्लोज भनेको के हो?” सोध्नुहोस्',
  ],
  // TODO(speaker review): drafted 2026-10-13 for the Copy and Listen row under
  // an answer. Controls and one short note, read at the moment of deciding
  // what a tap does; `answerListenStop` is `stopAnswer`'s own word.
  answerActions: 'उत्तरका विकल्प',
  answerCopy: 'कपी गर्नुहोस्',
  answerCopied: 'कपी भयो',
  answerCopyFailed: 'कपी गर्न सकिएन',
  answerListen: 'सुन्नुहोस्',
  answerListenStop: 'रोक्नुहोस्',
  answerVoiceFailed: 'अहिले आवाज उपलब्ध छैन',
  attach: 'Attach a statement',
  removeAttachment: 'Remove',
  attachedFile: '{name} · {size}',
  reading: 'तपाईंको पोर्टफोलियो पढ्दै',
  uploading: 'तपाईंको विवरण पठाउँदै',
  phaseThinking: 'प्रश्न पढ्दै छु',
  phaseMarket: 'NEPSE को तथ्याङ्क पढ्दै',
  phaseNews: 'समाचार पढ्दै',
  phaseRules: 'नियम पढ्दै',
  phaseFigures: 'हिसाब गर्दै',
  phaseWorking: 'काम गर्दै',
  // TODO(speaker review): the waiting list's steps, drafted 2026-10-26; the
  // first and last are the engine reference client's own drafts.
  phaseWriting: 'उत्तर लेख्दै',
  phaseVoice: 'मित्रको शैलीमा लेख्दै',
  phaseChecking: 'अंकहरू फेरि जाँच्दै',
  loading: 'तपाईंको पोर्टफोलियो पढ्दै…',
  answerFailed:
    'म अहिले यसको जवाफ दिन सक्दिन। यो मेरो तर्फको समस्या हो — ' +
    'स्क्रिनमा भइसकेको कुरा तपाईंकै हो।',
  answerIncomplete: 'जवाफ अधुरो छ — सकिनु अघि नै रोकियो। फेरि सोध्नुहोस्, म त्यहीँबाट लिन्छु।',
  disclosureTitle: 'सुरु गर्नु अघि',
  // disclosureBody: DELIBERATELY ABSENT. See the module docstring — this is
  // the licensing sentence and belongs to counsel, not to a draft.
  disclosureAccept: 'बुझेँ',
  // TODO(speaker review): drafted 2026-10-13 for the colour toggle.
  colourBlind: 'रङ छुट्याउन सजिलो',
  // TODO(speaker review): drafted 2026-10-14, the colour-blind control on a
  // chart, and the line saying what it does.
  colourBlindTitle: 'सजिलो रङ',
  colourBlindNote: 'नाफा नीलोमा, घाटा सुन्तलामा',
  sourceYourPortfolio: 'तपाईंको पोर्टफोलियो',
  headerMore: 'थप विकल्प',
  headerNote: 'AI · तपाईंको पोर्टफोलियो पढ्छ',
  unreachable:
    'म अहिले तपाईंको होल्डिङ पढ्न सक्दिन। तपाईंको पोर्टफोलियोमा केही ' +
    'गडबडी छैन — यो मेरो तर्फको समस्या हो।',
  noHoldings: 'NEPSE सँग तुलना गर्न मिल्ने मूल्य भएको कुनै होल्डिङ तपाईंसँग छैन।',
  nothingHere: 'यो पृष्ठमा अहिले देखाउने केही छैन।',
  sessionExpired: 'तपाईंको सेसन सकियो। पृष्ठ रिलोड गर्नुहोस्, म यहीँबाट लिन्छु।',
  engineSignInFailed:
    'मित्रले अहिले तपाईंको साइन-इन जाँच्न सकेन। यो हाम्रो तर्फको समस्या हो, तपाईंको होइन। ' +
    'कृपया केही समयपछि फेरि प्रयास गर्नुहोस्।',
  // TODO(speaker review): drafted 2026-10-31 for the soft launch.
  notInRollout: 'तपाईंको खातामा मित्र अझै खुलेको छैन।',
  engineAway: 'म अहिले जवाफ दिन सक्दिन। तपाईंको प्रश्न राखिएको छ, केही बेरमा फेरि सोध्नुहोस्।',
  engineAwayIdle: 'म अहिले जवाफ दिन सक्दिन। केही बेरमा फेरि सोध्नुहोस्।',
  workedThrough: '{n} चरण पूरा गरियो, {s} से. लाग्यो',
  workedThroughOne: 'एक चरण पूरा गरियो, {s} से. लाग्यो',
  sessionMissing:
    'यो पृष्ठमा म तपाईंको खातामा साइन इन भएको थिइनँ, त्यसैले तपाईंको ' +
    'होल्डिङबारे केही भन्न सक्दिन। तपाईंको पोर्टफोलियोमा केही गडबडी छैन — ' +
    'यो मेरो तर्फको समस्या हो।',
  newest: 'नयाँ',
  conversationLabel: 'मित्रसँगको कुराकानी',
  youAsked: 'तपाईंले सोध्नुभयो',
  // TODO(speaker review): the engine widget's drafts, carried unchanged.
  tagMitra: 'मित्र',
  tagPortfolio: 'पोर्टफोलियो',
  tagCompany: 'कम्पनी',
  tagMarket: 'बजार',
  tagScreen: 'छनोट',
  tagTax: 'कर',
  tagPlan: 'योजना',
  tagLearn: 'सिकाइ',
  tagFiles: 'तपाईंका फाइल',
  sourcesCheckedOne: '{n} वटा स्रोत हेरियो',
  sourcesCheckedMany: '{n} वटा स्रोत हेरियो',
  sourcesFailed: ', {n} वटा असफल',
  sourcesHide: 'लुकाउनुहोस्',
  replyThinking: 'ल, एकछिन सोचौँ त…|राम्रो कुरा सोध्नुभयो… एकछिन हेरौँ है त|ल अब ढिला नगरी हेरौँ…',
  replyStepFigures: 'अब अङ्कहरू एक-एक गरी मिलाऔँ है त…|हिसाब चाहिँ कति हुन्छ त भन्दा, हेर्दै छु…',
  replyStepChecking:
    'गलत कुरा भन्नु भएन, एकचोटि फेरि जाँचौँ…|डाटाले के भन्छ त भन्दा, फेरि जाँच्दै छु…',
  replyStepWords: 'अब यसलाई सजिलो भाषामा बुझौँ है त…|कसरी सजिलो गरी भन्ने त भन्दा, सोच्दै छु…',
  replyStepAlmost: 'ल, लगभग भयो…|बस एकछिन…',
  replyChatStart: 'ल, एकछिन है…|सोच्दै छु…',
  replyChatWords: 'अब भन्छु है त…|बस एकछिन…',
  replyWithheld:
    'मैले त्यो जवाफ फिर्ता लिएँ, किनकि त्यसको सबै कुरा पुष्टि गर्न सकिनँ। कृपया फेरि सोध्नुहोस्।',
  secondsElapsed: '{n} से.',
  mitraSaid: 'मित्र',
  closingPrice: 'बन्द मूल्य',
  // TODO(speaker review): drafted 2026-10-05 (wow retest 2, A2/C1), the
  // engine's own "वित्तीय विवरण अनुसार" without its postposition.
  filingStamp: 'वित्तीय विवरण',
  // TODO(speaker review): drafted 2026-10-05 when the market card's breadth
  // columns left hard-coded English; a Nepali speaker has not read them.
  marketAdvanced: 'बढेका',
  marketDeclined: 'घटेका',
  marketUnchanged: 'स्थिर',
  contextPortfolio: 'तपाईंको पोर्टफोलियो',
  refused: 'अस्वीकृत',
  widen: 'प्यानल फराकिलो पार्नुहोस्',
  narrow: 'प्यानल साँघुरो पार्नुहोस्',
  close: 'प्यानल बन्द गर्नुहोस्',
  // The braces are substitution points read by the renderer, not words.
  showingOf: '{total} मध्ये {shown} पङ्क्ति देखाइँदै',
  showAll: 'सबै {total} देखाउनुहोस्',
  withheld:
    'मैले त्यो जवाफ सकिनु अघि नै रोकेँ — यसले मैले पालना गर्ने जाँच पार ' +
    'गरेन। फेरि सोध्नुहोस्, म तपाईंको होल्डिङले देखाएकै कुरामा सीमित रहन्छु।',
  emptyTitle: 'नेपालको बजारबारे सोध्नुहोस्',
  // TODO(speaker review): drafted 2026-10-13 for the mobile-parity pass
  // (announcements, the answer menu, the low-quota line, the entry points'
  // scope chip and questions, the quick sheet's Open, the compare tray's
  // note); a Nepali speaker has not read them.
  srAnswering: 'मित्रले उत्तर दिँदै छ',
  srAnswerReady: 'उत्तर तयार छ',
  answerMore: 'थप',
  answerMenu: 'उत्तरका विकल्प',
  answerAskAbout: 'यसबारे सोध्नुहोस्',
  quotaLeftOne: 'आज {n} प्रश्न बाँकी छ',
  quotaLeftMany: 'आज {n} प्रश्न बाँकी छन्',
  quotaLeftNone: 'आज प्रश्न बाँकी छैन',
  scopeAbout: '{symbol} बारे',
  scopeRemove: 'यो प्रश्नबाट {symbol} हटाउनुहोस्',
  askAboutMarket: 'आज बजार कस्तो चल्यो?',
  askAboutNews: 'यो समाचारबारे बताउनुहोस्: {headline}',
  sheetOpen: '{symbol} को पृष्ठ खोल्नुहोस्',
  prompts: ['कित्ता भनेको के हो?', 'IPO को आवेदन कसरी दिने?', 'मूल्य किन रोकियो?'],
  // TODO(speaker review): the disclosure sentence. It names the boundary
  // between describing and recommending that this product is built on, and it
  // is the first thing every visitor reads. The ask narrowed on 2026-09-10,
  // since the licence claim came out of it: what a speaker now has to approve
  // is a statement about what THIS assistant will not do plus a pointer at
  // Signal Zone, not a legal claim about another surface. English until then,
  // because an unreviewed Nepali rendering of the boundary is worse than an
  // English one -- this repository has decided that twice.
  disclosureBody: EN.disclosureBody,
  // TODO(speaker review): a product name rather than prose, per its own
  // docstring, so the question is whether the mark is written in Devanagari at
  // all. Rendered here to match `mitraSaid`, which this draft also translates.
  emptyKicker: 'मित्र AI',
  // TODO(speaker review): drafted 2026-10-13 for the finance UX (parts B,
  // D, E): the market pill, quick sheet, glossary, compare tray, language
  // switch, voice, share, pins, board, history and feedback. No Nepali
  // speaker has read them.
  marketPill: 'नेप्से बजार',
  marketOpen: 'खुला',
  marketPreOpen: 'प्रि-ओपन',
  marketClosed: 'बन्द',
  marketHoliday: 'बिदा',
  marketOpensIn: '{time} मा खुल्छ',
  marketClosesIn: '{time} मा बन्द हुन्छ',
  durationHoursMinutes: '{h} घण्टा {m} मिनेट',
  durationMinutes: '{m} मिनेट',
  sheetClose: 'बन्द गर्नुहोस्',
  sheetLoading: 'खुल्दैछ…',
  sheetFailed: 'अहिले यो खोल्न सकिएन।',
  sheetIndex: 'यो सूचकाङ्क हो। छोटो विवरण सूचीकृत कम्पनीका लागि मात्र हो।',
  sheetUnknown: 'यो सङ्केतको कुनै सूचीकृत कम्पनी छैन।',
  sheetPrice: 'अन्तिम मूल्य',
  sheetHistory: 'मूल्यको इतिहास',
  sheetDayRange: 'आजको दायरा',
  sheetYearRange: 'वर्षभरिको दायरा',
  sheetLow: 'न्यून',
  sheetHigh: 'उच्च',
  sheetEps: 'प्रतिशेयर आम्दानी',
  sheetTurnover: 'कारोबार रकम',
  sheetHeld: 'तपाईंसँग {kitta} कित्ता छ',
  sheetAsOf: '{date} सम्मको',
  sheetAsk: '{symbol} बारे सोध्नुहोस्',
  sheetAskQuestion: '{symbol} बारे बताउनुहोस्।',
  sheetWatch: 'वाचलिस्ट',
  sheetCompare: 'तुलना',
  termOpen: '{term} को अर्थ',
  termCourse: 'कोर्स: {title}',
  termReadMore: 'थप पढ्नुहोस्',
  termFailed: 'यो शब्द खोल्न सकिएन।',
  trayLabel: 'तुलना सूची',
  // TODO(speaker review): reworded 2026-10-13 to the mobile app's label.
  trayCompare: 'मित्रलाई तुलना गर्न सोध्नुहोस्',
  trayRemove: '{symbol} हटाउनुहोस्',
  trayClear: 'खाली गर्नुहोस्',
  trayFull: 'सूचीमा तीनवटा मात्र अटाउँछ।',
  trayAdded: '{symbol} तुलनामा थपियो',
  trayQuestion: '{symbols} लाई छेउछेउमा तुलना गर्नुहोस्।',
  trayAnd: 'र',
  // TODO(speaker review): drafted 2026-10-14 for the translate icon's name.
  micHold: 'बोल्न थिच्नुहोस् वा थिचिराख्नुहोस्',
  micCheck: 'शब्द जाँचेर पठाउनुहोस्',
  // TODO(speaker review): drafted 2026-10-14 for the heard-words review.
  heardPick: 'तपाईंले यो भन्नुभएको हो',
  // TODO(speaker review): drafted 2026-10-14 for the Signal Zone tap.
  openSignalZone: 'सिग्नल जोन खोल्नुहोस्',
  openAnalyticsStudio: 'एनालिटिक्स स्टुडियो खोल्नुहोस्',
  // TODO(speaker review): drafted 2026-10-30 for the Studio's widget and portal taps.
  openStudioWidget: '{title} विजेट खोल्नुहोस्',
  // TODO(speaker review): drafted 2026-10-30 for a named dashboard's tap.
  openStudioDashboard: '{name} खोल्नुहोस्',
  openStockPage: '{symbol} खोल्नुहोस्',
  openPortfolio: 'पोर्टफोलियो खोल्नुहोस्',
  openMarket: 'बजार खोल्नुहोस्',
  openSectors: 'सेक्टर खोल्नुहोस्',
  openBrokers: 'ब्रोकर खोल्नुहोस्',
  openWatchlist: 'वाचलिस्ट खोल्नुहोस्',
  openTransactions: 'कारोबार खोल्नुहोस्',
  openAppPage: '{label} खोल्नुहोस्',
  heardNone: 'यीमध्ये कुनै होइन',
  heardUndo: 'आफूले भनेको राख्नुहोस्: {heard}',
  heardCheckNumber: 'यो अङ्क जाँच्नुहोस्',
  micTryAgain: 'फेरि प्रयास गर्नुहोस्',
  answerShare: 'सेयर गर्नुहोस्',
  answerShared: 'तस्बिर तयार भयो',
  answerShareFailed: 'तस्बिर बनाउन सकिएन',
  shareStamp: 'मनीमित्रको मित्रबाट · {date}',
  shareNotAdvice: 'जानकारी मात्र, सल्लाह होइन',
  shareTitle: 'मित्रको उत्तर',
  answerPin: 'पिन गर्नुहोस्',
  answerPinned: 'तपाईंको बोर्डमा पिन भयो',
  answerPinFull: 'तपाईंको बोर्ड भरियो',
  answerPinFailed: 'पिन गर्न सकिएन',
  boardTitle: 'मेरो बोर्ड',
  boardOpen: 'मेरो बोर्ड खोल्नुहोस्',
  boardEmpty: 'अहिलेसम्म केही पिन छैन। उत्तर पिन गर्नुहोस्, यहाँ ताजा तथ्याङ्क देखिन्छ।',
  boardRemove: 'हटाउनुहोस्',
  boardRemoved: 'बोर्डबाट हटाइयो',
  boardUnavailable: 'यो अब उपलब्ध छैन।',
  // TODO(speaker review): drafted 2026-10-14 for a pin that re-ran with nothing to draw.
  boardNotDrawn: 'अहिले यसका लागि देखाउने केही छैन।',
  // TODO(speaker review): drafted 2026-10-14 for a pin whose re-run failed.
  boardReadFailed: 'अहिले यो ताजा गर्न सकिएन।',
  boardFailed: 'बोर्ड खोल्न सकिएन।',
  undo: 'पूर्ववत् गर्नुहोस्',
  // TODO(speaker review): drafted 2026-10-13 for alerts, Watch and Remind
  // me (confirm in chat); a Nepali speaker has not read them.
  sheetAlert: 'अलर्ट',
  sheetAlertDraft: '{symbol} को अलर्ट राख्नुहोस्, मूल्य रु ',
  watchQuestion: '{symbol} लाई मेरो वाचलिस्टमा थप्नुहोस्',
  watchSymbol: '{symbol} वाचलिस्टमा थप्नुहोस्',
  remindQuestion: '{symbol} को {event} बारे मलाई सम्झाउनुहोस्',
  remindQuestionTitle: '{title} बारे मलाई सम्झाउनुहोस्',
  remindBookClosure: 'बुक क्लोज',
  remindAgm: 'साधारण सभा',
  remindRightShare: 'हकप्रद सेयर',
  remindIpo: 'आईपीओ',
  // TODO(speaker review): drafted 2026-10-13; the toast while Undo is open,
  // worded as under way because nothing has been written yet.
  confirmAlertPending: 'अलर्ट राख्दै…',
  confirmWatchPending: 'वाचलिस्टमा थप्दै…',
  confirmWatchChangePending: 'वाचलिस्टको परिवर्तन सुरक्षित गर्दै…',
  confirmReminderPending: 'सम्झना राख्दै…',
  confirmDashboardPending: 'Saving to Analytics Studio…',
  // TODO(speaker review): drafted 2026-10-13 for the model-free Remind me
  // and Watch; a Nepali speaker has not read them.
  proposalAsking: 'तयार गर्दै…',
  proposalFailed: 'अहिले यो तयार गर्न सकिएन।',
  remindersTitle: 'सम्झनाहरू',
  remindersOpen: 'सम्झनाहरू खोल्नुहोस्',
  remindersEmpty:
    'अहिलेसम्म कुनै सम्झना छैन। बुक क्लोज, साधारण सभा, हकप्रद सेयर वा आईपीओमा "सम्झाउनुहोस्" थिच्नुहोस्।',
  remindersFailed: 'सम्झनाहरू खोल्न सकिएन।',
  remindersDelete: 'मेटाउनुहोस्',
  remindersDeleted: 'सम्झना मेटाइयो',
  reminderEventOn: 'घटना {date}',
  reminderRemindOn: '{date} मा सम्झाउँछ',
  reminderOnTime: 'तोकिएकै मितिमा',
  reminderMoved: 'मिति सारियो',
  reminderCancelled: 'रद्द भयो',
  todayTitle: 'आज',
  todayDismiss: 'हटाउनुहोस्',
  todayAsk: 'यसबारे सोध्नुहोस्',
  todayAskQuestion: '{title} बारे बताउनुहोस्।',
  todayDue: 'आज एउटा सम्झना छ',
  pinQuote: '{symbol} मूल्य',
  pinFundamentals: '{symbol} आधारभूत विवरण',
  pinChart: '{symbol} मूल्यको इतिहास',
  pinDividends: '{symbol} लाभांश',
  pinIndex: '{symbol} सूचकाङ्क',
  pinHoldings: 'तपाईंका होल्डिङ',
  pinAllocation: 'तपाईंको क्षेत्रगत बाँडफाँड',
  pinPerformance: 'तपाईंको पोर्टफोलियोको प्रदर्शन',
  pinEntitlements: 'तपाईंका बुक क्लोज',
  pinMarket: 'बजारको झलक',
  // TODO(speaker review): drafted 2026-10-14 for the sectors and compare pins.
  pinSectors: 'क्षेत्रगत उतारचढाव',
  pinCompare: '{symbols} तुलना',
  // TODO(speaker review): drafted 2026-10-14 for the fundamentals compare pin.
  pinCompareFundamentals: '{symbols} आधारभूत तथ्यांक तुलना',
  historyTitle: 'इतिहास',
  newChat: 'नयाँ च्याट',
  historyOpen: 'इतिहास खोल्नुहोस्',
  historySearch: 'आफ्ना प्रश्न खोज्नुहोस्',
  historyEmpty: 'अहिलेसम्म कुनै कुराकानी छैन।',
  historyNoMatch: 'त्यससँग केही मिलेन।',
  historyHidden: 'यो कुराकानीको सामग्री लुकाइएको छ।',
  historyCard: 'यो उत्तरमा कार्ड थियो।',
  historyFailed: 'इतिहास खोल्न सकिएन।',
  historyOpenFailed: 'त्यो च्याट खोल्न सकिएन। फेरि प्रयास गर्नुहोस्।',
  // TODO(speaker review): drafted 2026-10-30 for deleting chats from History.
  historyDelete: 'यो च्याट मेटाउनुहोस्',
  historyDeleteTitle: 'यो च्याट मेटाउने?',
  historyDeleteBody: 'यो तपाईंको इतिहासबाट हटाइनेछ।',
  historyDeleteConfirm: 'मेटाउनुहोस्',
  historyClearAll: 'सबै इतिहास हटाउनुहोस्',
  historyClearTitle: 'इतिहासबाट सबै च्याट हटाउने?',
  historyClearConfirm: 'सबै हटाउनुहोस्',
  historyConfirmCancel: 'रद्द गर्नुहोस्',
  historyDeleteFailed: 'मेटाउन सकिएन। फेरि प्रयास गर्नुहोस्।',
  feedbackUp: 'उपयोगी',
  feedbackDown: 'उपयोगी भएन',
  feedbackThanks: 'बताउनुभएकोमा धन्यवाद।',
  feedbackWhy: 'के मिलेन?',
  feedbackWrongFigure: 'गलत तथ्याङ्क',
  feedbackDidNotAnswer: 'जवाफ दिएन',
  feedbackTooLong: 'धेरै लामो',
  feedbackWrongLanguage: 'गलत भाषा',
  feedbackOther: 'अन्य',
  // TODO(speaker review): interactive shapes, drafted 2026-10-18
  playTryYours: 'तपाईंका आफ्नै अङ्क प्रयोग गर्नुहोस्',
  playYours: 'तपाईंका अङ्क',
  // Draft, unreviewed. TODO(speaker review).
  playStale: 'माथिको उत्तर मित्रका अङ्कका लागि हो। तपाईंका अङ्कका लागि सोध्नुहोस्।',
  playReset: 'मित्रका अङ्कमा फर्कनुहोस्',
  playAsk: 'यसबारे सोध्नुहोस्',
  playEnter: 'अङ्क लेख्नुहोस्',
  playLegendPremise: 'तपाईंले रोजेको परिवर्तन',
  playLegendEstimate: 'तपाईंले राखेको दरमा गणना, पूर्वानुमान होइन',
  playLegendHistory: 'विगतका बन्द मूल्य, भविष्यमा के हुन्छ भनेको होइन',
  playLegendSale: 'कसैले नगरेको बिक्रीको पूर्वावलोकन',
  playStepThrough: 'एक-एक गरी हेर्नुहोस्',
  playSideBySide: 'छेउछाउ',
  playChange: '{symbol} परिवर्तन गर्नुहोस्',
  playWroteAbout: 'मित्रले {a} र {b} बारे लेख्यो',
  playAskAbout: '{a} र {b} बारे सोध्नुहोस्',
  playGuessPrompt: 'पहिले तपाईंको अनुमान',
  playGuessSkip: 'छोड्नुहोस्',
  playGuessYours: 'तपाईंको अनुमान',
  playGuessReal: 'के भयो',
  playEnterInvalid: 'यो अङ्क यहाँ प्रयोग गर्न मिल्दैन',
  // TODO(speaker review): control names and the ask-pair question, from mobile's play_copy.dart.
  playControlMove: 'यदि यो सर्‍यो भने',
  playControlQuantity: 'परिमाण',
  playControlPrice: 'मूल्य',
  playControlMonthly: 'प्रत्येक महिना',
  playControlYears: 'वर्ष',
  playControlStart: 'कति अघिदेखि',
  playControlRate: 'बढ्ने दर',
  // TODO(speaker review): drafts, Task 8.
  playRate: 'तपाईंको दर',
  playTarget: 'तपाईंको लक्ष्य',
  playGuessLock: 'मेरो अनुमान पक्का गर्नुहोस्',
  playGuessNone: 'यो सुरुवातका लागि उपलब्ध छैन',
  playAskPairQuestion: '{a} कि {b}, छेउछाउ राखेर',
  // TODO(speaker review): the entry's submit and the pending line, drafted 2026-10-18.
  playEnterUse: 'यो अङ्क प्रयोग गर्नुहोस्',
  playPending: 'तपाईंका अङ्क निकाल्दै',
  playControlScope: 'कुन सेयर',
  playControlHolding: 'सेयर',
  playFailed: 'तपाईंका अङ्क अहिले निकाल्न सकिएन',
  playSwapFailed: 'त्यो कम्पनी अहिले देखाउन सकिएन',
  storyStep: 'चरण {n} / {total}: {title}',
  storySeeFull: 'पूरा पृष्ठ हेर्नुहोस्',
  // TODO(speaker review): the story's rail and step buttons.
  storySteps: 'चरणहरू',
  storyCount: '{total} मध्ये {n}',
  storyNext: 'अर्को',
  storyPrevious: 'अघिल्लो',
  // TODO(speaker review): drafted 2026-10-26 for the Beginner / Expert choice
  // and the term defined under a beginner's answer.
  termBriefMore: '{term} बारे थप',
}

// Romanised Nepali, the register most users actually type in. Same gap as
// NE, for the same reason: nothing in the lexicons doc is display-quality
// prose, and none of this was composed here.
// TODO(counsel): translate and review every key in this table into
// romanised Nepali; disclosureBody is the licensing sentence and needs the
// most scrutiny, but all thirty-one keys are currently unsourced English
// fallbacks.
const NE_LATN: Copy = {
  // TODO(speaker review): drafted 2026-10-18 for the Stage, the page an
  // answer opens beside the thread. Section titles and the page's controls;
  // a speaker should read the controls as what a tap does.
  stageKindPrice: 'Mulya',
  stageKindHolding: 'Tapaiko share',
  stageKindCalendar: 'Aaune mitiharu',
  stageKindFigures: 'Mukhya tathyanka',
  stageKindBreadth: 'Aaja badhne ra ghatne',
  stageKindSectors: 'Kshetraharu',
  stageKindRing: 'Tapaiko share ko bandphand',
  stageKindTable: 'Bistarma',
  stageKindBars: 'Chheuchhau rakhera',
  stageKindDefinition: 'Yasko artha',
  // TODO(speaker review): the duel's words, drafted 2026-10-19.
  stageKindCompare: 'Chheuchhau',
  stageKindQuiz: 'Aafai janchnuhos',
  stageDuel: 'Dui kampani',
  chartAsTable: 'Talika ko rupma hernuhos',
  chartAsChart: 'Chart ko rupma hernuhos',
  chartDate: 'Miti',
  duelWindow: 'Awadhi',
  duelHoldings: 'Tapaika share',
  duelWatchlists: 'Tapaika watchlist',
  duelSearch: 'Kampani khojnuhos',
  duelSearchHint: 'Nam wa sanket lekhnuhos',
  duelClose: 'Banda garnuhos',
  stageKindExplain: 'Mitra bataunchha',
  stageCompany: 'Company ko bistrit jhalak',
  stageMarketToday: 'Aajako bajar',
  stagePortfolio: 'Tapaiko portfolio',
  // TODO(speaker review): draft
  stageSectorCompare: 'Tulana',
  stageEvents: 'Aaune ghatanaharu',
  stageLearn: 'Kura bujhnuhos',
  // TODO(speaker review): the general page's name, drafted 2026-10-18.
  stageAnswer: 'Mitra ko jawaf',
  // TODO(speaker review): the plain-words drafts of 2026-10-18.
  stageOpen: 'Pura page kholnu',
  stageShowing: 'Yo chat ko chheuma khulla chha',
  stageShowingLayer: 'Khulla chha',
  stageBack: 'Chat ma farkanus',
  stageExpand: 'Pura chauda',
  stageShrink: 'Chheu ma',
  stampStale: 'Antim update {date}',
  stampFromFiling: 'Filing',
  stageFromApp: 'App bata',
  stageLaying: 'Page milaudai chhu',
  stagePlainChart: 'Sadharan chart',
  stageRidgeChart: 'Dharsa drishya',
  stageMore: 'Yo uttar bata aru',
  // TODO(speaker review): drafted 2026-10-18 for the ridge's cost line.
  stageAverageCost: 'Tapaiko ausat lagat',
  // TODO(speaker review): drafted 2026-10-18 for a Stage section's proof
  // (Pramaan), the same words as mobile's `stage_copy.dart`.
  stagePramaanOpen: 'Pramaan',
  stagePramaanWhere: 'Pramaan: yo kahaa bata aayo',
  stagePramaanTitle: 'Pramaan',
  stagePramaanWhat: 'Ke',
  stagePramaanAsOf: 'Kahilesamma',
  stagePramaanState: 'Awastha',
  stagePramaanPeriod: 'Awadhi',
  stagePramaanSource: 'Kahan bata aayo',
  stagePramaanSourceMarket: 'MoneyMitra le padheko exchange ra kampaniko tathyanka',
  stagePramaanSourcePortfolio: 'MoneyMitra ma bhayeko tapaiko portfolio bibaran',
  ask: 'Je pani sodhnuhos…',
  // TODO(speaker review): drafted 2026-10-13; see NE's askFollowUp.
  askFollowUp: 'Arko prashna sodhnuhos…',
  mic: 'Speak',
  micStop: 'Stop',
  micCancel: 'Discard',
  micReplay: 'Hear it back',
  micPause: 'Pause',
  showText: 'Read it',
  hideText: 'Hide text',
  answerSpeaking: 'Getting the voice ready',
  micListening: 'Sundai chhu',
  micWorking: 'Tayar pardai chhu',
  // TODO(speaker review): drafted 2026-10-14 for the mic's half-second tail; see `NE`.
  micFinishing: 'Sakdai chhu…',
  micDenied:
    'Mitra le microphone chalauna sakena. Browser ko site setting ma, address bar ko chheu ma, anumati dinuhos ani pheri prayas garnuhos.',
  micSilent: 'Maile kehi suninan. Pheri prayas garnuhos, wa type garnuhos.',
  micUnavailable: 'Yo browser le yaha record garna sakdaina. Type garna saknuhunchha.',
  // TODO(speaker review): drafted 2026-10-14 for the mic's failures and its tap mode; see `NE`.
  micNoMic: 'Microphone bhetiyena. Jodnuhos, wa type garnuhos.',
  spokenQuestion: 'Bolera sodhiyeko',
  micInsecure: 'Awaj ka lagi surakshit (https) page chahinchha. Type garna saknuhunchha.',
  micShort: 'Bolda mic thichirakhnuhos, wa suru garna ekpatak ra rokna pheri thichnuhos.',
  micUnreachable: 'Mitra samma pugna sakiena. Pheri prayas garnuhos, wa type garnuhos.',
  micListeningTap: 'Sundai chhu · rokna mic pheri thichnuhos',
  deltaUp: 'up',
  deltaDown: 'down',
  passageSource: 'Source',
  passageUndated: 'no date given',
  portfolioTotal: 'Your portfolio',
  positionsHeld: '{count} positions',
  unpricedHoldings: '{count} more could not be priced',
  send: 'Pathaunuhos',
  stopAnswer: 'Roknuhos',
  // TODO(speaker review): drafted 2026-10-05; see NE's factsLow.
  factsLow: 'Nyun',
  factsHigh: 'Uchcha',
  factsTodaysRange: 'Aajako dayara',
  factsTradingRanges: 'Karobar dayara',
  factsIndex: 'Suchakanka',
  // TODO(speaker review): drafted 2026-10-05; see NE's groupMedian.
  groupMedian: 'MADHYAK',
  groupAverage: 'AUSAT',
  jumpToLatest: 'Jump to the latest',
  // TODO(speaker review): drafted 2026-10-05; see NE.
  whatIfHint: 'Sodhna slider chhodnuhos wa Enter thichnuhos.',
  // TODO(speaker review): drafted 2026-10-05; see NE's whatIfAsk.
  whatIfAsk: 'Sodhnuhos',
  askNext: 'Arko prashna',
  // TODO(speaker review): drafted 2026-10-14; see `NE`.
  suggestTry: 'Yesto sodhnus',
  suggestMore: 'Thap sujhav',
  // TODO(speaker review): drafted 2026-10-14; see `NE`.
  deskMarket: 'Aaja ko bajar',
  deskAskMarket: 'Aaja bajar kasto rahyo?',
  deskBreadth: '{up} badhe, {down} ghate',
  deskBook: 'Mero portfolio',
  deskBookToday: 'aaja',
  deskAskBook: 'Aaja mero portfolio kasto rahyo?',
  deskAddHoldings: 'Aafno share thapnuhos',
  // TODO(speaker review): reworded 2026-10-14; see `NE`.
  deskCompanies: 'Tapaika company',
  deskLearn: 'Sikau',
  deskRemindersBoard: 'Reminder · Board',
  deskPinned: '{n} pin gariyeko',
  deskNoReminder: 'Aaja kehi baki chhaina',
  deskContinue: 'Jari rakhnuhos: “{question}”',
  deskStrip: 'Aaja ko bajar',
  deskTry: 'Sodhnuhos “{question}”',
  // TODO(speaker review): drafted 2026-10-14 for Desk v2's tiles; see `NE`.
  deskCompaniesExplore: 'Herna layak company',
  deskOpenCompany: '{name}, company kholnuhos',
  // TODO(speaker review): drafted 2026-10-14 for the lead line's mark; see `NE`.
  // TODO(speaker review): drafted 2026-10-14 for answer emphasis; see `NE`.
  markUp: 'badheko',
  markDown: 'ghateko',
  // TODO(speaker review): reworded 2026-10-14, a tap opens the quick sheet; see `NE`.
  markOpensCompany: '{name}, bibaran dekhauchha',
  // TODO(speaker review): drafted 2026-10-14 for answer links (wire §13); see `NE`.
  markOpensPage: '{name}, {page} kholchha',
  markPageIndex: 'suchakankaharu',
  markPageSector: 'chhetraharu',
  markPageBroker: 'broker',
  markPageIpo: 'IPO',
  markPageTerm: 'shabdakosh',
  markPageSignalZone: 'Signal Zone',
  leadUp: 'Bhau badheko',
  leadDown: 'Bhau ghateko',
  leadDates: 'Mitiharu',
  leadLearn: 'Sikai',
  deskLearnTap: 'Sikna thichnuhos',
  deskUpcoming: 'Tapaiko share ka aagami miti',
  deskPinHint: 'Uttar pin garnuhos, yahi rahanchha',
  deskRemindHint: 'Kunai pani book close card bata reminder rakhnuhos',
  deskReminderCount: 'Reminder · {n}',
  deskEventBookClosure: 'Book close',
  deskEventAgm: 'Sadharan sabha',
  deskEventRight: 'Hakprad share',
  deskEventIpo: 'IPO nishkasan',
  deskPrevClose: 'Aghillo banda {value}',
  // TODO(speaker review): drafted 2026-10-26 for the why beside a move.
  whyItMoved: 'Kina chalyo',
  deskExamples: [
    'Sodhnuhos “NABIL ko bhau kati chha?”',
    'Sodhnuhos “Mero portfolio kasto chha?”',
    'Sodhnuhos “Book close bhaneko ke ho?”',
  ],
  // TODO(speaker review): drafted 2026-10-13; see NE's answerActions.
  answerActions: 'Uttar ka bikalpa',
  answerCopy: 'Copy garnus',
  answerCopied: 'Copy bhayo',
  answerCopyFailed: 'Copy garna sakiyena',
  answerListen: 'Sunnus',
  answerListenStop: 'Roknuhos',
  answerVoiceFailed: 'Ahile aawaj upalabdha chhaina',
  attach: 'Attach a statement',
  removeAttachment: 'Remove',
  attachedFile: '{name} · {size}',
  reading: 'tapaiko portfolio padhdai',
  uploading: 'tapaiko biwaran pathaudai',
  phaseThinking: 'prashna padhdai chu',
  phaseMarket: 'NEPSE ko tathyanka padhdai',
  phaseNews: 'samachar padhdai',
  phaseRules: 'niyam padhdai',
  phaseFigures: 'hisab gardai',
  phaseWorking: 'kaam gardai',
  // TODO(speaker review): drafted 2026-10-26; see `NE`.
  phaseWriting: 'uttar lekhdai',
  phaseVoice: 'Mitra ko shaili ma lekhdai',
  phaseChecking: 'anka pheri janchdai',
  loading: 'Tapaiko portfolio padhdai…',
  answerFailed:
    'Ma ahile yasko jawaf dina sakdina. Yo mero tarfako samasya ho — ' +
    'screen ma bhaisakeko kura tapaiko nai ho.',
  answerIncomplete:
    'Jawaf adhuro cha — sakinu aghi nai rokiyo. Feri sodhnuhos, ma tyahi bata linchu.',
  disclosureTitle: 'Suru garnu aghi',
  // disclosureBody: DELIBERATELY ABSENT. See the module docstring.
  disclosureAccept: 'Bujhe',
  // TODO(speaker review): drafted 2026-10-13; see `NE`.
  colourBlind: 'Rang chhutyauna sajilo',
  // TODO(speaker review): drafted 2026-10-14; see `NE`.
  colourBlindTitle: 'Sajilo rang',
  colourBlindNote: 'Nafa nilo ma, ghata suntala ma',
  sourceYourPortfolio: 'tapaiko portfolio',
  headerMore: 'Thap vikalpa',
  headerNote: 'AI · tapaiko portfolio padhcha',
  unreachable:
    'Ma ahile tapaiko holding padhna sakdina. Tapaiko portfolio ma kehi ' +
    'gadbadi chaina — yo mero tarfako samasya ho.',
  noHoldings: 'NEPSE sanga tulana garna milne mulya bhaeko kunai holding tapai sanga chaina.',
  nothingHere: 'Yo prishtha ma ahile dekhaune kehi chaina.',
  sessionExpired: 'Tapaiko session sakiyo. Prishtha reload garnuhos, ma yahi bata linchu.',
  engineSignInFailed:
    'Mitra le ahile tapaiko sign-in janchna sakena. Yo hamro tarfa ko samasya ho, tapaiko hoina. ' +
    'Kripaya kehi samaya pachhi feri prayas garnuhos.',
  // TODO(speaker review): drafted 2026-10-31 for the soft launch.
  notInRollout: 'Tapaiko khata ma Mitra ajhai khuleko chhaina.',
  engineAway:
    'Ma ahile jawaf dina sakdina. Tapaiko prashna rakhieko cha, kehi bera ma feri sodhnuhos.',
  engineAwayIdle: 'Ma ahile jawaf dina sakdina. Kehi bera ma feri sodhnuhos.',
  workedThrough: '{n} charan pura garyo, {s} s lagyo',
  workedThroughOne: 'Ek charan pura garyo, {s} s lagyo',
  sessionMissing:
    'Yo prishtha ma ma tapaiko khata ma sign in bhaeko thiina, tyasaile ' +
    'tapaiko holding bare kehi bhanna sakdina. Tapaiko portfolio ma kehi ' +
    'gadbadi chaina — yo mero tarfako samasya ho.',
  newest: 'Naya',
  conversationLabel: 'Mitra sanga ko kurakani',
  youAsked: 'Tapaile sodhnubhayo',
  // TODO(speaker review): the engine widget's drafts, carried unchanged.
  tagMitra: 'Mitra',
  tagPortfolio: 'Portfolio',
  tagCompany: 'Company',
  tagMarket: 'Bajar',
  tagScreen: 'Chhanot',
  tagTax: 'Kar',
  tagPlan: 'Yojana',
  tagLearn: 'Sikai',
  tagFiles: 'Tapaika file',
  sourcesCheckedOne: '{n} wata srot heriyo',
  sourcesCheckedMany: '{n} wata srot heriyo',
  sourcesFailed: ', {n} wata asafal',
  sourcesHide: 'Lukaunuhos',
  replyThinking:
    'La, ekchin sochau ta…|Ramro kura sodhnu bhayo… ekchin herau hai ta|La aba dhila nagari herau…',
  replyStepFigures:
    'Aba anka haru one by one milau hai ta…|Hisab chahi kati huncha ta bhanda, herdai chu…',
  replyStepChecking:
    'Galat kura bhannu bhayena, ekchoti feri check garau…|Data le ke bhancha ta bhanda, feri janchdai chu…',
  replyStepWords:
    'Aba yeslai sajilo bhasha ma bujhau hai ta…|Kasari simple gari bhanne ta bhanda, sochdai chu…',
  replyStepAlmost: 'La, lagbhag bhayo…|Bas ekchin…',
  replyChatStart: 'La, ekchin hai…|Sochdai chu…',
  replyChatWords: 'Aba bhanchu hai ta…|Bas ekchin…',
  replyWithheld:
    'Maile tyo jawaf phirta lie, kinaki tyasko sabai kura pushti garna sakina. ' +
    'Kripaya feri sodhnuhos.',
  secondsElapsed: '{n}s',
  mitraSaid: 'Mitra',
  closingPrice: 'Banda mulya',
  // TODO(speaker review): drafted 2026-10-05; the engine's prose says
  // "filing anusar", the channel keeping the English loan.
  filingStamp: 'Filing',
  // TODO(speaker review): drafted 2026-10-05; see NE.
  marketAdvanced: 'Badheka',
  marketDeclined: 'Ghateka',
  marketUnchanged: 'Sthir',
  contextPortfolio: 'Tapaiko portfolio',
  refused: 'Aswikrit',
  widen: 'Panel pharakilo parnuhos',
  narrow: 'Panel saghuro parnuhos',
  close: 'Panel banda garnuhos',
  showingOf: '{total} madhye {shown} pankti dekhaidai',
  showAll: 'Sabai {total} dekhaunuhos',
  withheld:
    'Maile tyo jawaf sakinu aghi nai roke — yasle maile palana garne janch ' +
    'par garena. Feri sodhnuhos, ma tapaiko holding le dekhaeko kura ma ' +
    'simit rahanchu.',
  emptyTitle: 'Nepal ko bajar bare sodhnuhos',
  // TODO(speaker review): drafted 2026-10-13 for the mobile-parity pass; see NE.
  srAnswering: 'Mitra le uttar dirdai chha',
  srAnswerReady: 'Uttar tayar chha',
  answerMore: 'Thap',
  answerMenu: 'Uttar ka vikalpa',
  answerAskAbout: 'Yesbare sodhnuhos',
  quotaLeftOne: 'Aaja {n} prashna baaki chha',
  quotaLeftMany: 'Aaja {n} prashna baaki chhan',
  quotaLeftNone: 'Aaja prashna baaki chhaina',
  scopeAbout: '{symbol} bare',
  scopeRemove: 'Yo prashna bata {symbol} hataunuhos',
  askAboutMarket: 'Aaja bazar kasto chalyo?',
  askAboutNews: 'Yo samachar bare bataunuhos: {headline}',
  sheetOpen: '{symbol} ko page kholnuhos',
  prompts: ['Kitta bhaneko ke ho?', 'IPO ko aawedan kasari dine?', 'Mulya kina rokiyo?'],
  // TODO(speaker review): see NE, including why the ask narrowed.
  disclosureBody: EN.disclosureBody,
  emptyKicker: 'Mitra AI',
  // TODO(speaker review): drafted 2026-10-13 for the finance UX, as in NE.
  marketPill: 'NEPSE bajar',
  marketOpen: 'Khula',
  marketPreOpen: 'Pre-open samaya',
  marketClosed: 'Banda',
  marketHoliday: 'Bida',
  marketOpensIn: '{time} ma khulcha',
  marketClosesIn: '{time} ma banda huncha',
  durationHoursMinutes: '{h} ghanta {m} minet',
  durationMinutes: '{m} minet',
  sheetClose: 'Banda garnuhos',
  sheetLoading: 'Khuldaichha…',
  sheetFailed: 'Ahile yo kholna sakiena.',
  sheetIndex: 'Yo suchakanka ho. Chhoto bibaran suchikrit company ko lagi matra ho.',
  sheetUnknown: 'Yo sanket ko kunai suchikrit company chhaina.',
  sheetPrice: 'Antim mulya',
  sheetHistory: 'Mulya ko itihas',
  sheetDayRange: 'Aaja ko dayara',
  sheetYearRange: 'Barsa bhari ko dayara',
  sheetLow: 'Nyun',
  sheetHigh: 'Uchcha',
  sheetEps: 'Prati share aamdani',
  sheetTurnover: 'Karobar rakam',
  sheetHeld: 'Tapaisanga {kitta} kitta chha',
  sheetAsOf: '{date} samma ko',
  sheetAsk: '{symbol} bare sodhnuhos',
  sheetAskQuestion: '{symbol} ko barema bataunuhos.',
  sheetWatch: 'Watchlist ma',
  sheetCompare: 'Tulana',
  termOpen: '{term} ko artha',
  termCourse: 'Course bata: {title}',
  termReadMore: 'Thap padhnuhos',
  termFailed: 'Yo shabda kholna sakiena.',
  trayLabel: 'Tulana suchi',
  // TODO(speaker review): reworded 2026-10-13; see NE.
  trayCompare: 'Mitra lai tulana garna sodhnuhos',
  trayRemove: '{symbol} hataunuhos',
  trayClear: 'Khali garnuhos',
  trayFull: 'Suchi ma tin wota matra atauncha.',
  trayAdded: '{symbol} tulana ma thapiyo',
  trayQuestion: '{symbols} lai chheu chheu ma tulana garnuhos.',
  trayAnd: 'ra',
  // TODO(speaker review): drafted 2026-10-14; see `NE`.
  micHold: 'Bolna thichnuhos wa thichirakhnuhos',
  micCheck: 'Shabda janchera pathaunuhos',
  // TODO(speaker review): drafted 2026-10-14; see `NE`.
  heardPick: 'Tapaile yo bhannubhayeko ho',
  // TODO(speaker review): drafted 2026-10-14; see `NE`.
  openSignalZone: 'Signal Zone kholnuhos',
  openAnalyticsStudio: 'Analytics Studio kholnuhos',
  // TODO(speaker review): drafted 2026-10-30; see `NE`.
  openStudioWidget: '{title} widget kholnuhos',
  // TODO(speaker review): drafted 2026-10-30; see `NE`.
  openStudioDashboard: '{name} kholnuhos',
  openStockPage: '{symbol} kholnuhos',
  openPortfolio: 'Portfolio kholnuhos',
  openMarket: 'Bazar kholnuhos',
  openSectors: 'Sector kholnuhos',
  openBrokers: 'Broker kholnuhos',
  openWatchlist: 'Watchlist kholnuhos',
  openTransactions: 'Karobar kholnuhos',
  openAppPage: '{label} kholnuhos',
  heardNone: 'Yimadhye kunai hoina',
  heardUndo: 'Aafule bhaneko rakhnuhos: {heard}',
  heardCheckNumber: 'Yo anka janchnuhos',
  micTryAgain: 'Feri prayas garnuhos',
  answerShare: 'Share garnuhos',
  answerShared: 'Tasbir tayar bhayo',
  answerShareFailed: 'Tasbir banauna sakiena',
  shareStamp: 'MoneyMitra ko Mitra bata · {date}',
  shareNotAdvice: 'Jankari matra, salah hoina',
  shareTitle: 'Mitra ko uttar',
  answerPin: 'Pin garnuhos',
  answerPinned: 'Tapaiko board ma pin bhayo',
  answerPinFull: 'Tapaiko board bhariyo',
  answerPinFailed: 'Pin garna sakiena',
  boardTitle: 'Mero board',
  boardOpen: 'Mero board kholnuhos',
  boardEmpty: 'Ahile samma kehi pin chhaina. Uttar pin garnuhos, yaha taja tathyanka dekhincha.',
  boardRemove: 'Hataunuhos',
  boardRemoved: 'Board bata hataiyo',
  boardUnavailable: 'Yo aba uplabdha chhaina.',
  // TODO(speaker review): drafted 2026-10-14; see `NE`.
  boardNotDrawn: 'Ahile yesko lagi dekhaune kehi chhaina.',
  // TODO(speaker review): drafted 2026-10-14; see `NE`.
  boardReadFailed: 'Ahile yo taja garna sakiena.',
  boardFailed: 'Board kholna sakiena.',
  undo: 'Purvavat garnuhos',
  // TODO(speaker review): drafted 2026-10-13 for alerts, Watch and Remind
  // me (confirm in chat); a Nepali speaker has not read them.
  sheetAlert: 'Alert rakhnuhos',
  sheetAlertDraft: '{symbol} ko alert rakhnuhos, mulya Rs ',
  watchQuestion: '{symbol} lai mero watchlist ma thapnuhos',
  watchSymbol: '{symbol} watchlist ma thapnuhos',
  remindQuestion: '{symbol} ko {event} barema malai samjhaunuhos',
  remindQuestionTitle: '{title} barema malai samjhaunuhos',
  remindBookClosure: 'book close',
  remindAgm: 'sadharan sabha',
  remindRightShare: 'hakprad share',
  remindIpo: 'IPO nishkasan',
  // TODO(speaker review): drafted 2026-10-13; see NE's confirmAlertPending.
  confirmAlertPending: 'Alert rakhdai…',
  confirmWatchPending: 'Watchlist ma thapdai…',
  confirmWatchChangePending: 'Watchlist ko pariwartan surakshit gardai…',
  confirmReminderPending: 'Samjhana rakhdai…',
  confirmDashboardPending: 'Saving to Analytics Studio…',
  // TODO(speaker review): drafted 2026-10-13 for the model-free Remind me
  // and Watch; a Nepali speaker has not read them.
  proposalAsking: 'Tayar gardai…',
  proposalFailed: 'Ahile yo tayar garna sakiena.',
  remindersTitle: 'Samjhana haru',
  remindersOpen: 'Samjhana haru kholnuhos',
  remindersEmpty:
    'Ahile samma kunai samjhana chhaina. Book close, sadharan sabha, hakprad share wa IPO ma "Samjhaunuhos" thichnuhos.',
  remindersFailed: 'Samjhana haru kholna sakiena.',
  remindersDelete: 'Metaunuhos',
  remindersDeleted: 'Samjhana metaiyo',
  reminderEventOn: 'Ghatana {date}',
  reminderRemindOn: '{date} ma samjhaucha',
  reminderOnTime: 'Tokiekai miti ma',
  reminderMoved: 'Miti sariyo',
  reminderCancelled: 'Radda bhayo',
  todayTitle: 'Aaja',
  todayDismiss: 'Hataunuhos',
  todayAsk: 'Yesko barema sodhnuhos',
  todayAskQuestion: '{title} ko barema bataunuhos.',
  todayDue: 'Aaja euta samjhana chha',
  pinQuote: '{symbol} mulya',
  pinFundamentals: '{symbol} aadharbhut bibaran',
  pinChart: '{symbol} mulya ko itihas',
  pinDividends: '{symbol} labhansha',
  pinIndex: '{symbol} suchakanka',
  pinHoldings: 'Tapaika holding',
  pinAllocation: 'Tapaiko sector bandfand',
  pinPerformance: 'Tapaiko portfolio ko pradarshan',
  pinEntitlements: 'Tapaika book close',
  pinMarket: 'Bajar ko jhalak',
  // TODO(speaker review): drafted 2026-10-14; see `NE`.
  pinSectors: 'Sector anusar utarchadhav',
  pinCompare: '{symbols} tulana',
  // TODO(speaker review): drafted 2026-10-14; see `NE`.
  pinCompareFundamentals: '{symbols} aadharbhut tathyanka tulana',
  historyTitle: 'Itihas',
  newChat: 'Naya chat',
  historyOpen: 'Itihas kholnuhos',
  historySearch: 'Aafna prashna khojnuhos',
  historyEmpty: 'Ahile samma kunai kurakani chhaina.',
  historyNoMatch: 'Tyesanga kehi milena.',
  historyHidden: 'Yo kurakani ko samagri lukaieko chha.',
  historyCard: 'Yo uttar ma card thiyo.',
  historyFailed: 'Itihas kholna sakiena.',
  historyOpenFailed: 'Tyo chat kholna sakiena. Pheri prayas garnuhos.',
  // TODO(speaker review): drafted 2026-10-30 for deleting chats from History.
  historyDelete: 'Yo chat metaunuhos',
  historyDeleteTitle: 'Yo chat metaune?',
  historyDeleteBody: 'Yo tapai ko itihas bata hataine chha.',
  historyDeleteConfirm: 'Metaunuhos',
  historyClearAll: 'Sabai itihas hataunuhos',
  historyClearTitle: 'Itihas bata sabai chat hataune?',
  historyClearConfirm: 'Sabai hataunuhos',
  historyConfirmCancel: 'Radda garnuhos',
  historyDeleteFailed: 'Metauna sakiena. Pheri prayas garnuhos.',
  feedbackUp: 'Upayogi',
  feedbackDown: 'Upayogi bhaena',
  feedbackThanks: 'Bataunubhaekoma dhanyabad.',
  feedbackWhy: 'Ke milena?',
  feedbackWrongFigure: 'Galat tathyanka',
  feedbackDidNotAnswer: 'Jawaf diena',
  feedbackTooLong: 'Dherai lamo',
  feedbackWrongLanguage: 'Galat bhasa',
  feedbackOther: 'Anya',
  // TODO(speaker review): interactive shapes, drafted 2026-10-18
  playTryYours: 'Tapaiko aafnai anka prayog garnuhos',
  playYours: 'Tapaiko anka',
  // Draft, unreviewed. TODO(speaker review).
  playStale: 'Mathi ko uttar Mitra ko anka ko lagi ho. Tapaiko anka ko lagi sodhnuhos.',
  playReset: 'Mitraka ankama pharkanuhos',
  playAsk: 'Yesbare sodhnuhos',
  playEnter: 'Anka lekhnuhos',
  playLegendPremise: 'Tapaile rojeko pariwartan',
  playLegendEstimate: 'Tapaile rakheko darma ganana, purwanuman hoina',
  playLegendHistory: 'Bigatka banda mulya, bhabisyama ke huncha bhaneko hoina',
  playLegendSale: 'Kasaile nagareko bikriko purwawalokan',
  playStepThrough: 'Ek-ek gari hernuhos',
  playSideBySide: 'Chheuchhau',
  playChange: '{symbol} pariwartan garnuhos',
  playWroteAbout: 'Mitrale {a} ra {b} bare lekhyo',
  playAskAbout: '{a} ra {b} bare sodhnuhos',
  playGuessPrompt: 'Pahile tapaiko anuman',
  playGuessSkip: 'Chhodnuhos',
  playGuessYours: 'Tapaiko anuman',
  playGuessReal: 'Ke bhayo',
  playEnterInvalid: 'Yo anka yahan prayog garna mildaina',
  // TODO(speaker review): control names and the ask-pair question, from mobile's play_copy.dart.
  playControlMove: 'Yedi yo sare bhane',
  playControlQuantity: 'Parimaan',
  playControlPrice: 'Mulya',
  playControlMonthly: 'Pratyek mahina',
  playControlYears: 'Barsha',
  playControlStart: 'Kati aghi dekhi',
  playControlRate: 'Badhne dar',
  // TODO(speaker review): drafts, Task 8.
  playRate: 'Tapaiko dar',
  playTarget: 'Tapaiko lakshya',
  playGuessLock: 'Mero anuman pakka garnuhos',
  playGuessNone: 'Yo suruwatko lagi uplabdha chhaina',
  playAskPairQuestion: '{a} ki {b}, chheuchhau rakhera',
  // TODO(speaker review): the entry's submit and the pending line, drafted 2026-10-18.
  playEnterUse: 'Yo anka prayog garnuhos',
  playPending: 'Tapaiko anka nikaldai',
  playControlScope: 'Kun share',
  playControlHolding: 'Share',
  playFailed: 'Tapaiko anka ahile nikalna sakiena',
  playSwapFailed: 'Tyo company ahile dekhauna sakiena',
  storyStep: 'Charan {n} / {total}: {title}',
  storySeeFull: 'Pura prishtha hernuhos',
  // TODO(speaker review): the story's rail and step buttons.
  storySteps: 'Charanharu',
  storyCount: '{total} madhye {n}',
  storyNext: 'Arko',
  storyPrevious: 'Aghillo',
  // TODO(speaker review): drafted 2026-10-26 for the Beginner / Expert choice
  // and the term defined under a beginner's answer; see `NE`.
  termBriefMore: '{term} bare thap',
}

const CATALOGUE: Record<Lang, Copy> = { en: EN, ne: NE, 'ne-latn': NE_LATN }

/** The catalogue a turn's script is drawn in: one register per alphabet. */
const LANG_OF_SCRIPT: Record<TurnScript, Lang> = {
  latin: 'en',
  devanagari: 'ne',
  romanized: 'ne-latn',
}

/**
 * The register one turn's labels are drawn in.
 *
 * THE ENGINE'S WORD FIRST. The ack states the script the turn is answered in
 * (`AckFrame.script`, S0's `detect_script`, measured over the channel's own
 * corpus), so a romanized question gets romanized labels without this side
 * reading a single word, and an English question gets English labels whatever
 * the empty-screen toggle was left on: the panel's language is a fact about
 * the host page, not about the question.
 *
 * Without one (an archived turn, an engine before the field) the alphabet is
 * read: Devanagari letters outnumbering Latin ones is Nepali, and a ticker in
 * a Nepali question does not tip it. A Latin-script question is English unless
 * the panel says romanized, because telling romanized Nepali from English
 * takes a word list and the owner rejects hand-made ones; the alphabet alone
 * still rules out Devanagari labels over a Latin question. A question with no
 * letters at all keeps the panel's language.
 */
export function langOfQuestion(question: string, panel: Lang, script?: TurnScript | null): Lang {
  // String-keyed: an engine may name a script this build has no catalogue for.
  if (script) return (LANG_OF_SCRIPT as Record<string, Lang | undefined>)[script] ?? panel
  const devanagari = question.match(/(?=\p{Script=Devanagari})[\p{L}\p{M}]/gu)?.length ?? 0
  const latin = question.match(/\p{Script=Latin}/gu)?.length ?? 0
  if (devanagari > latin) return 'ne'
  if (latin > 0) return panel === 'ne-latn' ? 'ne-latn' : 'en'
  return panel
}

export function copy(lang: Lang): Copy {
  // Read string-keyed: the record is exhaustive over `Lang` by type, but a
  // lang the type system ruled out can still arrive from storage or a URL,
  // and English is what it falls back to.
  return (CATALOGUE as Record<string, Copy>)[lang] ?? EN
}

/**
 * Keys still carrying English because no reviewed translation exists.
 *
 * Exported so the suite can assert it rather than a person remembering to.
 * The disclosure sentence is the one that matters most: it states the licence
 * boundary, and a machine translation of it is a licensing statement nobody
 * who can read it has reviewed.
 */
export const AWAITING_TRANSLATION: Record<Exclude<Lang, 'en'>, (keyof Copy)[]> = {
  // Everything else was translated on 2026-08-28 and is this repository's
  // draft, marked TODO(speaker review) at each catalogue rather than listed
  // here: this array is the record of what is still the ENGLISH string, and a
  // drafted Nepali one is not that. `disclosureBody` is, and stays, because a
  // machine translation of a licensing sentence is a licensing risk nobody
  // reviewed.
  // The seven mic keys join it on 2026-08-29 rather than being drafted like
  // their neighbours. Every one of them is read at the moment something has
  // already gone wrong — no microphone, no sound, no transcript — and a
  // machine-shaped Nepali sentence is worst exactly there, where the reader
  // is deciding whether the thing is broken or they are.
  // `jumpToLatest` joins them on 2026-08-30 for the same reason the mic keys
  // are here rather than drafted: it is a control, not prose, and a
  // machine-shaped imperative on a button is read as an instruction the
  // reader must get right. Left in English until a speaker gives it one.
  // The 2026-09-02 render-layer keys. Four are read at the moment the
  // reader is checking whether they gained or lost money, or whether to leave
  // the app; the fifth counts the turn's own work. A machine-shaped word is
  // worst exactly there, so all five stay English until a speaker gives them
  // one — the same ground the mic keys are here on.
  // `attach` and `removeAttachment` join on 2026-09-04 on `jumpToLatest`'s own
  // ground: controls, not prose, read at the moment of deciding what a tap
  // does. `attachedFile` is a template rather than a sentence, and the same
  // caution applies to a template nobody has reviewed as one.
  // `askFollowUp` joined on 2026-09-29 and left on 2026-10-13: an English
  // placeholder under every Nepali answer (13b-13e) read as the panel not
  // knowing the register, which is worse than a draft a speaker still owes.
  ne: [
    'disclosureBody',
    'jumpToLatest',
    'attach',
    'removeAttachment',
    'attachedFile',
    'mic',
    'micStop',
    'micCancel',
    'micReplay',
    'micPause',
    'showText',
    'hideText',
    'answerSpeaking',
    'deltaUp',
    'deltaDown',
    'passageSource',
    'passageUndated',
    'portfolioTotal',
    'positionsHeld',
    'unpricedHoldings',
    // The dashboard's toast (2026-10-18), English until a speaker words it.
    'confirmDashboardPending',
  ],
  // Two extra entries here and not under `ne`, and neither is a gap. This
  // array is defined by string identity with the English, and in ROMANIZED
  // Nepali a proper noun is identical to English by being correct: "Mitra"
  // romanises to "Mitra". The Devanagari catalogue writes मित्र, so it differs
  // there and does not appear. Listing them keeps the inventory's own rule —
  // it records what is byte-for-byte the English string — rather than
  // exempting two keys and making the rule mean something looser.
  'ne-latn': [
    'disclosureBody',
    'jumpToLatest',
    'attach',
    'removeAttachment',
    'attachedFile',
    'emptyKicker',
    'mitraSaid',
    // The reply frame's words that romanise to the English: proper nouns and
    // loanwords, listed on the ground given above for `mitraSaid`.
    'tagMitra',
    'tagPortfolio',
    'tagCompany',
    // A link mark's page names (wire §13, 2026-10-14) that romanise to the
    // English on the same ground: a loanword (broker), an acronym (IPO) and
    // the surface's own name (Signal Zone).
    'markPageBroker',
    'markPageIpo',
    'markPageSignalZone',
    'secondsElapsed',
    'mic',
    'micStop',
    'micCancel',
    'micReplay',
    'micPause',
    'showText',
    'hideText',
    'answerSpeaking',
    'deltaUp',
    'deltaDown',
    'passageSource',
    'passageUndated',
    'portfolioTotal',
    'positionsHeld',
    'unpricedHoldings',
    // The dashboard's toast (2026-10-18), English until a speaker words it.
    'confirmDashboardPending',
  ],
}
